import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  searchLedgerPatients,
  getPatientLedger,
  getHouseholdLedger,
  syncPatientLedgerFromPMS,
  getLatestSettled,
  createInvoice,
  getPracticeInfo,
  listPatientPaymentMethods,
  sendInvoiceEmail,
  sendInvoiceSms,
  logInvoiceActivity,
  updateLedgerPaymentProvider,
  splitLedgerPayment,
  deleteLedgerPayment,
  bulkInvoiceFromLedger,
} from '../services/paymentsService.js';
import { parseBackendDate, FRONTEND_BASE_URL } from '../services/common.js';
import Icon from './Icon';
import KpiTile from './KpiTile';
import Pager from './Pager';

const CLAIM_STATE_STYLES = {
  PAID: { bg: '#e7f4ec', color: '#1d6b40', label: 'Paid' },
  SUBMITTED: { bg: '#e8f0f7', color: '#1f4e79', label: 'Submitted' },
  CREATED: { bg: '#fdf3e3', color: '#a8520c', label: 'Created' },
  REJECTED: { bg: '#fdecea', color: '#912018', label: 'Rejected' },
  RECEIVED: { bg: '#f1ecfb', color: '#5b3fa8', label: 'Received' },
};

const TXN_TYPE_STYLES = {
  PROCEDURE: { bg: '#e8f0f7', color: '#1f4e79', label: 'Procedure' },
  PAYMENT: { bg: '#e7f4ec', color: '#1d6b40', label: 'Payment' },
  ADJUSTMENT: { bg: '#fdf3e3', color: '#a8520c', label: 'Adjustment' },
  WRITE_OFF: { bg: '#fdeee4', color: '#9a3412', label: 'Write-off' },
  REFUND: { bg: '#fdecea', color: '#912018', label: 'Refund' },
};

const formatCurrency = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const parseDateLoose = (iso) => {
  if (!iso) return null;
  if (DATE_ONLY_RE.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (iso) => {
  const d = parseDateLoose(iso);
  if (!d) return iso || '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// "Today, 9:32 AM" / "Yesterday, 4:10 PM", else the full date-time.
const formatSettledAt = (iso) => {
  const d = parseDateLoose(iso);
  if (!d) return iso || '—';
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const today = new Date();
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay(d, today)) return `Today, ${time}`;
  if (sameDay(d, yesterday)) return `Yesterday, ${time}`;
  return formatDateTime(iso);
};

// "America/Los_Angeles" -> { short: 'PDT', long: 'Pacific Daylight Time' },
// evaluated on the given day so it says PST/PDT correctly across DST. Zones
// without a US-style abbreviation come back as e.g. "GMT+1"; an unknown
// zone id falls back to itself.
const describeTimezone = (timeZone, isoDate) => {
  if (!timeZone) return null;
  const at = parseDateLoose(isoDate) || new Date();
  at.setHours(12, 0, 0, 0); // midday — never the DST switch-over hour
  const name = (style) => {
    try {
      return new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: style })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value;
    } catch {
      return null;
    }
  };
  return { short: name('short') || timeZone, long: name('long') || timeZone };
};

const SETTLED_PAGE_SIZE = 25;

// Calendar date + clock time of ``d`` in ``timeZone`` (the practice's zone,
// from the ledger response), or in the browser's zone when none is given.
// Ledger timestamps render in the practice's zone: Open Dental rows are dated
// at the practice's local midnight, which a browser west of the practice
// would show as 9 PM the day before.
const zonedParts = (d, timeZone) => {
  if (timeZone) {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone, year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
      }).formatToParts(d).forEach((p) => { parts[p.type] = Number(p.value); });
      return { y: parts.year, m: parts.month, day: parts.day, h: parts.hour % 24, min: parts.minute };
    } catch {
      // unknown zone id: fall through to the browser's zone
    }
  }
  return { y: d.getFullYear(), m: d.getMonth() + 1, day: d.getDate(), h: d.getHours(), min: d.getMinutes() };
};

// YYYY-MM-DD of ``iso`` in ``timeZone``; date-only strings are already
// calendar dates and pass through unchanged.
const dateKeyIn = (iso, timeZone) => {
  if (!iso) return '';
  if (DATE_ONLY_RE.test(iso)) return iso;
  const d = parseDateLoose(iso);
  if (!d) return iso;
  const p = zonedParts(d, timeZone);
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
};

const formatDate = (iso, timeZone) => {
  const d = parseDateLoose(iso);
  if (!d) return iso || '—';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (timeZone && !DATE_ONLY_RE.test(iso)) {
    try {
      return d.toLocaleDateString('en-US', { ...opts, timeZone });
    } catch {
      // unknown zone id: fall through to the browser's zone
    }
  }
  return d.toLocaleDateString('en-US', opts);
};

const formatTimeIfNotMidnight = (iso, timeZone) => {
  const d = parseDateLoose(iso);
  if (!d) return null;
  if (DATE_ONLY_RE.test(iso)) return null;
  const { h, min } = zonedParts(d, timeZone);
  if (h === 0 && min === 0) return null;
  const opts = { hour: 'numeric', minute: '2-digit' };
  if (timeZone) {
    try {
      return d.toLocaleTimeString('en-US', { ...opts, timeZone });
    } catch {
      // unknown zone id: fall through to the browser's zone
    }
  }
  return d.toLocaleTimeString('en-US', opts);
};

const isMeaningfulSubtype = (s) =>
  s && !/UNSPECIFIED/i.test(s);

const prettyCustomType = (raw) => {
  if (!raw) return null;
  const tail = String(raw).split('/').pop() || '';
  const cleaned = tail.replace(/^pay-/i, '').replace(/^cust(om)?-/i, '').replace(/[-_]/g, ' ').trim();
  if (!cleaned) return null;
  return cleaned
    .split(' ')
    .map((w) => (w === 'ins' ? 'Insurance' : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
};

const customTypeLabel = (event) => {
  const desc = (event && event.custom_type_description) || '';
  if (desc) return desc.trim();
  return prettyCustomType(event && event.custom_type);
};

const txnDescription = (event) => {
  if (event.procedure_description) {
    return event.procedure_code
      ? `${event.procedure_code} — ${event.procedure_description}`
      : event.procedure_description;
  }
  if (event.note) return event.note;

  if (event.transaction_type === 'PAYMENT') {
    const subtype = (event.transaction_subtype || '').toUpperCase();
    if (subtype.includes('INSURANCE')) {
      const carrier = event.related_claim_carrier;
      const claimId = event.related_claim_remote_id;
      const parts = ['Insurance Payment'];
      if (carrier) parts.push(`from ${carrier}`);
      if (claimId) parts.push(`for claim #${claimId}`);
      return parts.join(' ');
    }
    const custom = customTypeLabel(event);
    if (custom) return custom;
    return 'Patient Payment';
  }

  if (isMeaningfulSubtype(event.transaction_subtype)) {
    return event.transaction_subtype
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const custom = customTypeLabel(event);
  if (custom) return custom;

  if (event.transaction_type) {
    const t = event.transaction_type.toLowerCase();
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  return '—';
};

const initialsFor = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorForName = (name) => {
  const palette = ['#dbeafe', '#dcfce7', '#fef3c7', '#fce7f3', '#ede9fe', '#e0f2fe', '#fee2e2'];
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const colorForNameDark = (name) => {
  const palette = ['#1e3a8a', '#14532d', '#78350f', '#831843', '#4c1d95', '#075985', '#7f1d1d'];
  let h = 0;
  const s = String(name || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

const formatPhone = (raw) => {
  if (!raw) return '';
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return String(raw);
};

const claimDescription = (event) => {
  const parts = [];
  if (event.carrier) parts.push(event.carrier);
  if (event.payer_claim_id) parts.push(`Payer Claim ${event.payer_claim_id}`);
  if (event.group_name) parts.push(`Group: ${event.group_name}`);
  return parts.join(' · ') || '—';
};

const SummaryCard = ({ label, value, sub, accent }) => (
  <div
    style={{
      backgroundColor: '#fff',
      border: '1px solid #e3e7e4',
      borderRadius: 8,
      padding: '14px 16px',
      flex: '1 1 180px',
      minWidth: 180,
    display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }}
  >
    <div style={{ fontSize: 12, color: '#6b716d', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, color: accent || '#1a1d1b' }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#6b716d' }}>{sub}</div>}
  </div>
);

const Pill = ({ bg, color, children, title }) => (
  <span
    title={title}
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      backgroundColor: bg,
      color,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const LedgerTab = ({
  practiceId,
  onRequestCharge,
  openInvoiceRequest,
  onInvoiceRequestConsumed,
  // Headless "modal-only" mode: render just the invoice-preview flow overlays
  // (no page chrome / patient modal) so callers like the AR Aging tab can
  // reuse the exact same invoice popup in place. `onClose` fires once the
  // whole flow is dismissed so the caller can unmount this instance.
  modalOnly = false,
  onClose,
  // Preselected patient ({ contact_id, name, phone, email }) — when provided
  // (with modalOnly), open straight into that patient's statement modal
  // instead of the search UI. Used by the Invoice tab's clickable name.
  initialPatient = null,
  // Optional Set of ledger check_numbers Newton posted (the invoices'
  // ``ledgerPost.reference``). Ledger rows whose check_number is in this set —
  // or that carry the ``Newtonai`` marker prefix — are tinted light green so
  // staff can see at a glance which rows Newton wrote to the PMS.
  newtonReferences = null,
  // { contactId, data } pushed by a parent that is already refetching this
  // patient's ledger (Invoice tab, while a Newton post is still landing).
  // Applied in place so the open statement gains the row without a reload.
  liveLedger = null,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchBoxRef = useRef(null);
  const debounceRef = useRef(null);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState(null);

  const [householdLedger, setHouseholdLedger] = useState(null);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [householdError, setHouseholdError] = useState(null);

  // "Sync from PMS" button state. The ledger reads from our DB cache; this
  // button live-reads Kolla, persists to the cache, then re-fetches the
  // ledger so staff can pull the latest Dentrix data on demand.
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  // OpenDental-ledger practices refresh asynchronously (the backend queues the
  // practice refresh); show a neutral notice and reload once it has had time.
  const [syncNotice, setSyncNotice] = useState(null);
  const syncReloadTimer = useRef(null);
  useEffect(() => () => clearTimeout(syncReloadTimer.current), []);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  // 'patient' | 'family' — Open-Dental-style toggle. Defaults to 'patient'.
  const [ledgerView, setLedgerView] = useState('patient');

  const [eventFilter, setEventFilter] = useState('all'); // all | transactions | claims
  // Invoice preview ({ kind, name, contact_id, view, dateKeys, events, totals,
  // balanceDue }) — null when closed. Opened from either the modal "Create
  // Invoice" button or from the row-level "Invoice" button on Latest Settled.
  const [invoicePreview, setInvoicePreview] = useState(null);
  // Set when the user clicks Invoice on a Latest Settled row before the
  // ledger has finished loading. Stores { contactId, view } so we can
  // auto-open the preview as soon as the right ledger is ready.
  const [pendingInvoiceFor, setPendingInvoiceFor] = useState(null);
  // Tracks the in-flight POST when the user clicks Create Invoice from the
  // preview, plus the result of the most recent attempt:
  //   { success: true, invoice }  — created (we surface a public link)
  //   { success: false, error }   — show inline error, allow retry
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(null);
  // After a statement invoice is created we offer to send the payment link
  // to the patient. Stashes { invoice, patientName, phone, email } while the
  // "Send payment link" modal is open (SMS / Email / Both); null = closed.
  const [sendLinkModal, setSendLinkModal] = useState(null);
  // Which channel is mid-send ('sms' | 'email' | 'both'), so we can disable
  // the buttons and show a spinner label. null = idle.
  const [sendingChannel, setSendingChannel] = useState(null);
  // Inline result banner inside the send modal: { type: 'success'|'error',
  // message }. null = nothing shown yet.
  const [sendFeedback, setSendFeedback] = useState(null);
  // When the backend reports {conflict: true} because the patient already
  // has a pending invoice, we stash the existing-invoice info here so the
  // confirmation dialog can show "Replace existing pending invoice for
  // $X created Y ago?". null = no conflict pending.
  const [pendingConflict, setPendingConflict] = useState(null);
  // Optional single-date override for the Date column on the invoice
  // preview. null = keep each row's actual transaction/claim date (default
  // behavior). When set to a YYYY-MM-DD string, every line-item row shows
  // the chosen date in the preview AND every line item we POST is stamped
  // with that date so the public invoice mirrors it. Carry-over/Balance
  // Due summary rows are unaffected (they always render as "—").
  const [invoiceDateOverride, setInvoiceDateOverride] = useState(null);

  // Provider re-credit on green (Newton-posted) ledger rows. Tracks the
  // remote_id currently being saved (for the spinner/disabled state) and the
  // last error so we can surface it inline near the row's dropdown.
  const [providerSavingId, setProviderSavingId] = useState(null);
  const [providerEditError, setProviderEditError] = useState(null);
  // remote_id of the row whose provider dropdown is currently unlocked for
  // editing. null = every green row is read-only (guards against accidental
  // provider changes). Set by the inline "Edit" button.
  const [editingProviderId, setEditingProviderId] = useState(null);

  // "Break down" modal: split one Newton-posted payment into several rows,
  // each credited to a different provider (deletes the original + creates N
  // in the PMS). ``splitModal`` = { event, total } for the row being split;
  // ``splitLines`` = [{ amount, providerId }].
  const [splitModal, setSplitModal] = useState(null);
  const [splitLines, setSplitLines] = useState([]);
  const [splitSaving, setSplitSaving] = useState(false);
  const [splitError, setSplitError] = useState(null);
  // Set when the split lines don't sum to the original amount and staff try
  // to proceed — drives the "balance doesn't match" confirmation popup.
  const [unbalancedConfirm, setUnbalancedConfirm] = useState(null);
  // remote_id of the Newton-posted row currently being deleted (spinner /
  // disabled state), and the last delete error surfaced inline.
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [deleteRowError, setDeleteRowError] = useState(null);

  // Auto-charge support — practice-level toggle + the patient's default
  // saved card. We fetch both lazily when an invoice preview opens so
  // the checkbox can show the actual card it'll charge ("•• 4242 — $123")
  // rather than a generic placeholder. If the practice has the toggle
  // off OR the patient has no card on file, the checkbox is hidden.
  const [practicePaymentSettings, setPracticePaymentSettings] = useState({
    autoChargeOnFileEnabled: false,
    requireDobToPay: false,
    surchargeEnabled: false,
    ledgerProviders: [],
  });
  const [previewDefaultCard, setPreviewDefaultCard] = useState(null);
  const [autoChargeRequested, setAutoChargeRequested] = useState(false);

  const [latestSettled, setLatestSettled] = useState(null);
  const [settledLoading, setSettledLoading] = useState(false);
  const [settledError, setSettledError] = useState(null);
  const [settledCollapsed, setSettledCollapsed] = useState(false);
  const [settledFilter, setSettledFilter] = useState('all'); // all | owes | settled
  const [settledPage, setSettledPage] = useState(1);
  // null = "use latest settlement date"; otherwise YYYY-MM-DD chosen by the user.
  const [selectedDate, setSelectedDate] = useState(null);

  // Bulk "Invoice & send" on the settled list (backend: ledger_bulk_invoice).
  // The selection holds contact ids of rows that show an Invoice button;
  // results are keyed by contact id so each row can show what happened to it.
  const [bulkSelected, setBulkSelected] = useState(() => new Set());
  const [bulkChannels, setBulkChannels] = useState('both'); // both | sms | email | none
  const [bulkReview, setBulkReview] = useState(null); // dry-run response
  const [bulkBusy, setBulkBusy] = useState(null); // 'review' | 'send' | null
  const [bulkError, setBulkError] = useState(null);
  const [bulkResults, setBulkResults] = useState(null); // { byContact, summary }

  const runSearch = useCallback(
    async (q) => {
      if (!practiceId) return;
      setSearchLoading(true);
      setSearchError(null);
      try {
        const data = await searchLedgerPatients(practiceId, q, { limit: 20 });
        if (data && data.success) {
          setSearchResults(data.patients || []);
        } else {
          setSearchError(data?.error || 'Search failed');
          setSearchResults([]);
        }
      } catch (err) {
        console.error('Patient search error:', err);
        setSearchError(err.message || 'Search failed');
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [practiceId]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!showResults) return undefined;
    debounceRef.current = setTimeout(() => {
      runSearch(searchQuery);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, showResults, runSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    if (showResults) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showResults]);

  // Patient modal: close on Escape + lock body scroll while open.
  const patientModalOpen = Boolean(selectedPatient);
  useEffect(() => {
    if (!patientModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSelectedPatient(null);
        setLedger(null);
        setLedgerError(null);
        setSearchQuery('');
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [patientModalOpen]);

  const loadPatientLedger = useCallback(
    async (contactId) => {
      if (!practiceId || !contactId) return;
      setLedgerLoading(true);
      setLedgerError(null);
      try {
        const data = await getPatientLedger(practiceId, contactId);
        if (data && data.success) {
          setLedger(data);
        } else {
          setLedgerError(data?.error || 'Failed to load patient ledger');
          setLedger(null);
        }
      } catch (err) {
        console.error('Patient ledger error:', err);
        setLedgerError(err.message || 'Failed to load patient ledger');
        setLedger(null);
      } finally {
        setLedgerLoading(false);
      }
    },
    [practiceId]
  );

  // Re-credit a Newton-posted payment (green row) to a different provider.
  // Writes to the PMS via the backend, then updates the row in place. We
  // patch local state optimistically rather than reloading the ledger — the
  // reload does a slow live PMS read (~seconds), and the only thing that
  // changed is this one row's provider. The 5-min cron reconciles the rest.
  const handleChangeRowProvider = useCallback(
    async (event, newProviderId) => {
      const remoteId = event?.remote_id;
      if (!remoteId || !newProviderId || newProviderId === event.provider_id) return;
      setProviderSavingId(remoteId);
      setProviderEditError(null);
      try {
        const res = await updateLedgerPaymentProvider(practiceId, remoteId, newProviderId);
        if (!res || !res.success) {
          throw new Error(res?.error || 'Failed to change provider');
        }
        setEditingProviderId(null);
        const providerName =
          (practicePaymentSettings.ledgerProviders || []).find((p) => p.id === newProviderId)?.name ||
          newProviderId;
        const patchEvents = (data) =>
          data && Array.isArray(data.events)
            ? {
                ...data,
                events: data.events.map((e) =>
                  e.remote_id === remoteId
                    ? { ...e, provider_id: newProviderId, provider_name: providerName }
                    : e
                ),
              }
            : data;
        setLedger((prev) => patchEvents(prev));
        setHouseholdLedger((prev) => patchEvents(prev));
      } catch (err) {
        console.error('Change provider error:', err);
        setProviderEditError({ remoteId, message: err.message || 'Failed to change provider' });
      } finally {
        setProviderSavingId(null);
      }
    },
    [practiceId, practicePaymentSettings]
  );

  // --- Delete a Newton-posted row from the PMS -----------------------------
  const handleDeleteRow = useCallback(
    async (event) => {
      const remoteId = event?.remote_id;
      if (!remoteId) return;
      const amt = formatCurrency(Math.abs(Number(event.amount) || 0));
      if (
        !window.confirm(
          `Delete this Newton-posted payment (${amt}) from the PMS? This can’t be undone.`
        )
      )
        return;
      setDeletingRowId(remoteId);
      setDeleteRowError(null);
      try {
        const res = await deleteLedgerPayment(practiceId, remoteId);
        if (!res || !res.success) {
          throw new Error(res?.error || 'Failed to delete payment');
        }
        // Optimistically drop the row from local state (no reload).
        const dropEvent = (data) =>
          data && Array.isArray(data.events)
            ? { ...data, events: data.events.filter((e) => e.remote_id !== remoteId) }
            : data;
        setLedger((prev) => dropEvent(prev));
        setHouseholdLedger((prev) => dropEvent(prev));
      } catch (err) {
        console.error('Delete row error:', err);
        setDeleteRowError({ remoteId, message: err.message || 'Failed to delete payment' });
      } finally {
        setDeletingRowId(null);
      }
    },
    [practiceId]
  );

  // --- Break down (split) a Newton-posted payment into per-provider rows ---
  const openSplitModal = useCallback((event) => {
    const total = Math.abs(Number(event.amount) || 0);
    setSplitError(null);
    setSplitModal({ event, total });
    // Seed with the original provider carrying the full amount, plus one
    // empty line to split into. Staff adjust from there.
    setSplitLines([
      { amount: total ? total.toFixed(2) : '', providerId: event.provider_id || '' },
      { amount: '', providerId: '' },
    ]);
  }, []);

  const closeSplitModal = useCallback(() => {
    if (splitSaving) return;
    setSplitModal(null);
    setSplitLines([]);
    setSplitError(null);
    setUnbalancedConfirm(null);
  }, [splitSaving]);

  const updateSplitLine = useCallback((idx, field, value) => {
    setSplitLines((lines) =>
      lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l))
    );
  }, []);

  const addSplitLine = useCallback(() => {
    setSplitLines((lines) => [...lines, { amount: '', providerId: '' }]);
  }, []);

  const removeSplitLine = useCallback((idx) => {
    setSplitLines((lines) =>
      lines.length <= 2 ? lines : lines.filter((_, i) => i !== idx)
    );
  }, []);

  const submitSplit = useCallback(async ({ force = false } = {}) => {
    if (!splitModal) return;
    const { event, total } = splitModal;
    const remoteId = event?.remote_id;

    // Validate line shape: >=2 lines, each with a provider + positive amount.
    const cleaned = splitLines.map((l) => ({
      providerId: (l.providerId || '').trim(),
      amount: Number(l.amount),
    }));
    if (cleaned.length < 2) {
      setSplitError('Add at least two lines to break down this payment.');
      return;
    }
    if (cleaned.some((l) => !l.providerId)) {
      setSplitError('Every line needs a provider.');
      return;
    }
    if (cleaned.some((l) => !(l.amount > 0))) {
      setSplitError('Every line needs an amount greater than 0.');
      return;
    }

    const sumCents = cleaned.reduce((s, l) => s + Math.round(l.amount * 100), 0);
    const totalCents = Math.round(total * 100);
    const balanced = sumCents === totalCents;

    // If the lines don't add up to the original, require an explicit
    // confirmation before proceeding (it changes the patient's ledger
    // balance). The confirm dialog re-invokes this with force=true.
    if (!balanced && !force) {
      setSplitError(null);
      setUnbalancedConfirm({ sumCents, totalCents });
      return;
    }

    setSplitSaving(true);
    setSplitError(null);
    try {
      const res = await splitLedgerPayment(
        practiceId,
        remoteId,
        cleaned.map((l) => ({ providerId: l.providerId, amount: l.amount.toFixed(2) })),
        { allowUnbalanced: !balanced }
      );
      if (!res || !res.success) {
        throw new Error(res?.error || 'Failed to break down payment');
      }
      setUnbalancedConfirm(null);
      setSplitModal(null);
      setSplitLines([]);
      // Refresh in the background (don't block the UI on a live PMS read).
      // The backend already patched the cache with the slice rows, so this
      // reload just reconciles running balances a moment later.
      const cid = selectedPatient?.contact_id;
      if (cid) loadPatientLedger(cid);
    } catch (err) {
      console.error('Split payment error:', err);
      setUnbalancedConfirm(null);
      setSplitError(err.message || 'Failed to break down payment');
    } finally {
      setSplitSaving(false);
    }
  }, [splitModal, splitLines, practiceId, selectedPatient, loadPatientLedger]);

  const loadHouseholdLedger = useCallback(
    async (contactId) => {
      if (!practiceId || !contactId) return;
      setHouseholdLoading(true);
      setHouseholdError(null);
      try {
        const data = await getHouseholdLedger(practiceId, contactId);
        if (data && data.success) {
          setHouseholdLedger(data);
        } else {
          setHouseholdError(data?.error || 'Failed to load household ledger');
          setHouseholdLedger(null);
        }
      } catch (err) {
        console.error('Household ledger error:', err);
        setHouseholdError(err.message || 'Failed to load household ledger');
        setHouseholdLedger(null);
      } finally {
        setHouseholdLoading(false);
      }
    },
    [practiceId]
  );

  // Pull the latest transactions + claims from the PMS for the open
  // patient, then reload whichever ledger view is showing. The backend
  // persists the live PMS data into our cache and the GET endpoints read
  // DB-only, so a successful sync followed by a re-fetch surfaces the
  // fresh data. Household members are synced server-side in one shot.
  const handleSyncFromPMS = useCallback(async () => {
    if (!practiceId || !selectedPatient?.contact_id || syncing) return;
    const contactId = selectedPatient.contact_id;
    setSyncing(true);
    setSyncError(null);
    setSyncNotice(null);
    try {
      const res = await syncPatientLedgerFromPMS(practiceId, contactId);
      if (!res || !res.success) {
        setSyncError(res?.error || 'Sync failed');
        return;
      }
      if (res.queued) {
        setSyncNotice(res.message || 'Refreshing from the practice management system. Updates appear in about a minute.');
        clearTimeout(syncReloadTimer.current);
        syncReloadTimer.current = setTimeout(() => {
          setHouseholdLedger(null);
          loadPatientLedger(contactId);
          setLastSyncedAt(Date.now());
          setSyncNotice(null);
        }, 75000);
        return;
      }
      // Cache is now fresh. Always reload the patient ledger (its balance
      // is already household-aware, built server-side). Clearing the
      // household ledger lets the lazy effect refetch it when the Family
      // tab is (or becomes) active, without double-fetching here.
      setHouseholdLedger(null);
      await loadPatientLedger(contactId);
      setLastSyncedAt(Date.now());
      if (res.partial) {
        setSyncError('Some records could not be synced from the PMS. Showing the latest available.');
      }
    } catch (err) {
      console.error('Sync from PMS error:', err);
      setSyncError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }, [practiceId, selectedPatient, syncing, loadPatientLedger]);

  const handleSelectPatient = (patient, options = {}) => {
    const { defaultView, autoInvoice, invoiceRange } = options;
    setSelectedPatient(patient);
    setSearchQuery(patient.name);
    setShowResults(false);
    // Family-first: callers that know the patient belongs to a household can
    // pass `defaultView: 'family'` so the modal opens straight on the Family
    // tab. Falls back to Patient when nothing was specified.
    const view = defaultView === 'family' ? 'family' : 'patient';
    setLedgerView(view);
    setHouseholdLedger(null);
    setHouseholdError(null);
    setInvoicePreview(null);
    setCreatingInvoice(false);
    setInvoiceCreated(null);
    setInvoiceDateOverride(null);
    setPendingConflict(null);
    setPendingInvoiceFor(autoInvoice ? { contactId: patient.contact_id, view, range: invoiceRange || null } : null);
    setSyncError(null);
    setLastSyncedAt(null);
    loadPatientLedger(patient.contact_id);
  };

  // Parent-driven refresh of the open statement. Silent: no loading flash.
  useEffect(() => {
    if (!liveLedger?.data?.success || !selectedPatient?.contact_id) return;
    if (String(liveLedger.contactId) !== String(selectedPatient.contact_id)) return;
    setLedger(liveLedger.data);
    setLedgerLoading(false);
    setLedgerError(null);
  }, [liveLedger, selectedPatient]);

  // Headless entry point: when a caller mounts us with a preselected patient
  // (e.g. the Invoice tab clicking a patient name), open that patient's
  // statement modal immediately — the same read-only ledger view staff get
  // from the Ledger-tab search. Fires once per mount.
  const initialPatientOpenedRef = useRef(false);
  useEffect(() => {
    if (!initialPatient || !initialPatient.contact_id) return;
    if (initialPatientOpenedRef.current) return;
    initialPatientOpenedRef.current = true;
    handleSelectPatient(initialPatient);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPatient]);

  // External deep-link into the invoice flow (e.g. the AR Aging tab's
  // "Send invoice" button). Selects the household head and auto-opens the
  // family statement invoice preview — the same popup staff get from the
  // ledger — then clears the request so it can't re-fire on a later remount.
  useEffect(() => {
    if (!openInvoiceRequest?.contactId) return;
    handleSelectPatient(
      { contact_id: openInvoiceRequest.contactId, name: openInvoiceRequest.name || '' },
      // `range` ({ start, end }) opens the statement on that date window
      // instead of the default last-2-days-of-activity (e.g. the Schedule
      // tab passes visit date → today).
      { defaultView: 'family', autoInvoice: true, invoiceRange: openInvoiceRequest.range }
    );
    if (onInvoiceRequestConsumed) onInvoiceRequestConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openInvoiceRequest?.contactId, openInvoiceRequest?.ts]);

  // Modal-only lifecycle: remember once the (background) patient/family modal
  // has opened so we can (a) stop showing the brief "Opening…" placeholder and
  // (b) tell the caller to unmount us the moment the whole stacked flow —
  // patient modal + invoice preview + conflict + send-link — is dismissed.
  const invoiceFlowOpenedRef = useRef(false);
  useEffect(() => {
    if (selectedPatient) invoiceFlowOpenedRef.current = true;
  }, [selectedPatient]);
  useEffect(() => {
    if (!modalOnly || !onClose) return;
    if (!invoiceFlowOpenedRef.current) return;
    if (selectedPatient || invoicePreview || pendingConflict || sendLinkModal) return;
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOnly, selectedPatient, invoicePreview, pendingConflict, sendLinkModal]);

  // Centralized close so we always clear in-flight state alongside the
  // preview itself. Both modal-overlay clicks and the footer Close button
  // route through this.
  const closeInvoicePreview = useCallback(() => {
    setInvoicePreview(null);
    setCreatingInvoice(false);
    setInvoiceCreated(null);
    setInvoiceDateOverride(null);
    setPendingConflict(null);
  }, []);

  // Modal-only: once the send-payment-link modal is dismissed (after choosing
  // SMS / Email / Both, or skipping), tear down the whole stack — the invoice
  // preview and the background patient/family modal — so the caller (e.g. AR
  // Aging) drops straight back to its board instead of leaving two modals open.
  // On the full Ledger page this stays untouched: the preview remains so staff
  // can see the "Invoice created" banner.
  const sendLinkShownRef = useRef(false);
  useEffect(() => {
    if (sendLinkModal) {
      sendLinkShownRef.current = true;
      return;
    }
    if (!modalOnly || !sendLinkShownRef.current) return;
    sendLinkShownRef.current = false;
    closeInvoicePreview();
    setSelectedPatient(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOnly, sendLinkModal]);

  // Lazily load the family ledger the first time the user clicks the Family
  // tab for a given patient. We re-fetch whenever the selected patient
  // changes so we can't accidentally show another household's data.
  useEffect(() => {
    if (ledgerView !== 'family' || !selectedPatient?.contact_id) return;
    if (householdLedger || householdLoading) return;
    loadHouseholdLedger(selectedPatient.contact_id);
  }, [ledgerView, selectedPatient, householdLedger, householdLoading, loadHouseholdLedger]);

  // Build an invoice preview from the loaded ledger.
  //
  // - When no `customRange` is passed we default to the most recent two
  //   distinct days of activity (Open Dental's "last visit + most recent
  //   statement period" idea), so the preview reads like a statement for
  //   the patient/family's latest visits rather than their entire history.
  // - When the user edits the range pickers in the modal header we rebuild
  //   with their explicit `{ start, end }` (YYYY-MM-DD), filtering every
  //   event whose date falls inside that window. The carry-over math stays
  //   correct because `previousBalance = patient_balance - netChange` is
  //   derived from the actual current balance regardless of window size.
  const buildInvoicePreview = useCallback((sourceLedger, view, patientInfo, customRange = null) => {
    if (!sourceLedger || !sourceLedger.events) return null;

    const dateKeyOf = (e) =>
      dateKeyIn(e.transaction_date || e.claim_time || e.occurred_at, sourceLedger.timezone);

    let events;
    let dateKeys;

    if (customRange && customRange.start && customRange.end) {
      // Normalize so start <= end regardless of how the user typed them.
      const lo = customRange.start <= customRange.end ? customRange.start : customRange.end;
      const hi = customRange.start <= customRange.end ? customRange.end : customRange.start;
      events = sourceLedger.events.filter((e) => {
        const k = dateKeyOf(e);
        return k && k >= lo && k <= hi;
      });
      // Distinct dates that actually have activity in the window — used by
      // the "(only N day of activity)" hint and the carry-over caption.
      const seen = [];
      for (const e of events) {
        const k = dateKeyOf(e);
        if (k && !seen.includes(k)) seen.push(k);
      }
      // Newest-first to match the default branch's ordering.
      seen.sort((a, b) => b.localeCompare(a));
      dateKeys = seen;
      // Allow rebuilding even if the user picked a window with zero events
      // (we still need to render the empty-state row + carry-over).
    } else {
      const seen = [];
      for (const e of sourceLedger.events) {
        const k = dateKeyOf(e);
        if (k && !seen.includes(k)) seen.push(k);
        if (seen.length >= 2) break;
      }
      dateKeys = seen;
      if (dateKeys.length === 0) return null;
      events = sourceLedger.events.filter((e) => dateKeys.includes(dateKeyOf(e)));
    }

    // Subtotals across just the events in the preview window. These describe
    // what's on this statement; they do NOT replace the customer's full
    // running balance (that's the actual amount due).
    let production = 0;
    let adjustments = 0;
    let insurancePayments = 0;
    let patientPayments = 0;
    for (const e of events) {
      if (e.kind !== 'transaction') continue;
      const amt = Number(e.amount) || 0;
      const t = (e.transaction_type || '').toUpperCase();
      const sub = (e.transaction_subtype || '').toUpperCase();
      if (t === 'CHARGE' || t === 'PROCEDURE') production += amt;
      else if (t === 'ADJUSTMENT') adjustments += amt;
      else if (t === 'PAYMENT' && sub.includes('INSURANCE')) insurancePayments += amt;
      else if (t === 'PAYMENT') patientPayments += amt;
    }

    const totalBalance = sourceLedger.summary?.patient_balance ?? 0;

    // Net change of the events inside the preview window. Payments and
    // credits are stored with their natural sign in event.amount (payments
    // are negative), so totals are already signed and we can just add them.
    const netChange = production + adjustments + insurancePayments + patientPayments;
    // Anything older than the window is rolled into a carry-over so that
    //   previousBalance + netChange === balanceDue
    // and the line items on the invoice add up to the actual amount due.
    const previousBalance = Math.round((totalBalance - netChange) * 100) / 100;

    // Family-aware billing model:
    //   - Family view  → bill the guarantor of the household (one
    //                    statement covering every member's activity).
    //   - Patient view → bill the patient directly, even when they're a
    //                    dependent. The Patient tab now uses family math
    //                    for its balance, so the addressee is the only
    //                    thing that differs from the Family tab — staff
    //                    who open a specific patient's row clearly want
    //                    that patient on the invoice (their own name,
    //                    phone, and email), not the guarantor.
    const householdGuarantor =
      view === 'family' ? sourceLedger.guarantor : null;
    const billedToGuarantor = Boolean(householdGuarantor?.contact_id);

    const billToContactId = billedToGuarantor
      ? householdGuarantor.contact_id
      : patientInfo?.contact_id;
    const billToName = billedToGuarantor
      ? householdGuarantor.name || patientInfo?.name || 'Guarantor'
      : patientInfo?.name || 'Patient';

    return {
      kind: view === 'family' ? 'family' : 'patient',
      view,
      contact_id: patientInfo?.contact_id,
      // Who the invoice is actually charged to (guarantor whenever one
      // exists, mirroring Open Dental).
      billToContactId,
      billToName,
      // True iff we redirected the bill from the patient to their guarantor.
      // Used by the modal to show a "Bill to: <Guarantor> (head of household)"
      // hint so the user understands why the addressee differs from the
      // patient they opened.
      billedToGuarantor,
      onBehalfOfName: billedToGuarantor ? patientInfo?.name || null : null,
      // Display name (includes "(Family)" suffix in family mode).
      name: view === 'family' ? `${billToName} (Family)` : billToName,
      patientPhone: patientInfo?.phone || '',
      patientEmail: patientInfo?.email || '',
      dateKeys,
      // start = older / end = newer. When the user supplied an explicit
      // range, honor it verbatim (even if there's no activity at the
      // edges) so the inputs round-trip exactly. Otherwise derive from the
      // discovered activity days.
      dateRange:
        customRange && customRange.start && customRange.end
          ? {
              start:
                customRange.start <= customRange.end
                  ? customRange.start
                  : customRange.end,
              end:
                customRange.start <= customRange.end
                  ? customRange.end
                  : customRange.start,
            }
          : {
              start: dateKeys[dateKeys.length - 1],
              end: dateKeys[0],
            },
      // True only when the range was the auto-default (last 2 days of
      // activity), so the modal can show a "reset" link only when the
      // user has actually customized it.
      isDefaultRange: !customRange,
      // Stashed so the modal can rebuild the preview when the user edits
      // the date range pickers without needing closure access to the
      // original args.
      _source: { sourceLedger, view, patientInfo },
      events,
      totals: {
        production: Math.round(production * 100) / 100,
        adjustments: Math.round(adjustments * 100) / 100,
        insurancePayments: Math.round(insurancePayments * 100) / 100,
        patientPayments: Math.round(patientPayments * 100) / 100,
        netChange: Math.round(netChange * 100) / 100,
      },
      previousBalance,
      balanceDue: totalBalance,
    };
  }, []);

  const openInvoicePreview = useCallback(
    (sourceLedger, view, patientInfo, range = null) => {
      const preview = buildInvoicePreview(sourceLedger, view, patientInfo, range);
      if (preview) {
        setInvoicePreview(preview);
        setCreatingInvoice(false);
        setInvoiceCreated(null);
        setInvoiceDateOverride(null);
      }
    },
    [buildInvoicePreview]
  );

  // Rebuild the invoice preview with a different date window. Triggered by
  // the From/To date inputs in the modal header. Passing `null` resets back
  // to the auto-default (last 2 days of activity).
  const updateInvoiceRange = useCallback(
    (nextRange) => {
      if (!invoicePreview || !invoicePreview._source) return;
      const { sourceLedger, view, patientInfo } = invoicePreview._source;
      const next = buildInvoicePreview(sourceLedger, view, patientInfo, nextRange);
      if (next) setInvoicePreview(next);
    },
    [invoicePreview, buildInvoicePreview]
  );

  // Load the practice payment toggles once per mount. These rarely
  // change, so we don't refetch on every preview open — the autoCharge
  // checkbox just respects whatever was loaded here.
  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getPracticeInfo(practiceId);
        if (cancelled) return;
        const incoming = data?.practice?.payments;
        if (incoming) {
          setPracticePaymentSettings({
            autoChargeOnFileEnabled: !!incoming.autoChargeOnFileEnabled,
            requireDobToPay: !!incoming.requireDobToPay,
            surchargeEnabled: !!incoming.surchargeEnabled,
            ledgerProviders: Array.isArray(incoming.ledgerProviders)
              ? incoming.ledgerProviders
              : [],
          });
        }
      } catch (e) {
        // Toggles default to off; non-fatal.
      }
    })();
    return () => { cancelled = true; };
  }, [practiceId]);

  // Whenever the preview switches to a different patient (or opens for
  // the first time), look up their default card on file. Cleared back
  // to null when the preview closes so a stale card from a previous
  // patient never appears under a new name.
  useEffect(() => {
    if (!invoicePreview) {
      setPreviewDefaultCard(null);
      setAutoChargeRequested(false);
      return;
    }
    if (!practicePaymentSettings.autoChargeOnFileEnabled) {
      setPreviewDefaultCard(null);
      setAutoChargeRequested(false);
      return;
    }
    const patientId = invoicePreview.billToContactId || invoicePreview.contact_id;
    if (!practiceId || !patientId) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await listPatientPaymentMethods(practiceId, patientId);
        if (cancelled) return;
        if (result?.success) {
          const cards = (result.paymentMethods || []).filter(c => c.status === 'ACTIVE');
          const def = cards.find(c => c.isDefault) || cards[0] || null;
          setPreviewDefaultCard(def);
          // Pre-check the box only when there's actually a card to use.
          // The user can untick if they want to send the link instead.
          setAutoChargeRequested(!!def);
        } else {
          setPreviewDefaultCard(null);
          setAutoChargeRequested(false);
        }
      } catch (e) {
        setPreviewDefaultCard(null);
        setAutoChargeRequested(false);
      }
    })();
    return () => { cancelled = true; };
  }, [invoicePreview, practiceId, practicePaymentSettings.autoChargeOnFileEnabled]);

  // POSTs the preview to the same /api/payments/invoices endpoint the
  // Transactions tab uses. We translate ledger events into the line-item
  // shape the backend expects: PROCEDURE/CHARGE → positive line, ADJUSTMENT
  // → as-signed, PAYMENT → negative line (with isInsurance flag). Family
  // invoices are billed to the guarantor and prefix each line with the
  // family member it was posted to so the recipient can read the statement.
  //
  // `options.supersede` opts in to replacing any existing pending invoice
  // for the same patient. The first attempt always passes false; on
  // duplicate-pending conflict the user is prompted, and a confirmed
  // retry passes true.
  const handleCreateInvoice = useCallback(async (options = {}) => {
    const { supersede = false, chargeAfter = false } = options;
    if (!practiceId || !invoicePreview || creatingInvoice) return;
    if (!(invoicePreview.balanceDue > 0)) return;

    setCreatingInvoice(true);
    setInvoiceCreated(null);
    if (!supersede) {
      // Clear any stale conflict state on a fresh attempt; if the backend
      // reports a conflict again we'll re-set it below.
      setPendingConflict(null);
    }

    try {
      const lineItems = [];
      let subtotalProcedures = 0;
      let totalAdjustments = 0;
      let totalInsurancePayments = 0;
      let totalPatientPayments = 0;

      // Carry-over from before the preview window. Without this row the
      // line items would only sum to the in-window net change, not the
      // actual balance due, and the invoice math would look broken.
      const previousBalance = invoicePreview.previousBalance || 0;
      if (Math.abs(previousBalance) >= 0.005) {
        lineItems.push({
          description:
            invoicePreview.kind === 'family'
              ? `Previous family balance brought forward (before ${formatDate(
                  invoicePreview.dateRange.start
                )})`
              : `Previous balance brought forward (before ${formatDate(
                  invoicePreview.dateRange.start
                )})`,
          quantity: 1,
          price: previousBalance,
          code: '',
          type: 'CARRYOVER',
          date: null,
        });
      }

      const labelFor = (event) => {
        const base =
          event.kind === 'transaction' ? txnDescription(event) : claimDescription(event);
        if (invoicePreview.kind === 'family' && event.posted_to_name) {
          return `[${event.posted_to_name}] ${base}`;
        }
        return base;
      };

      for (const event of invoicePreview.events) {
        // Only transactions become invoice line items. Standalone claim
        // events in the timeline are informational (SUBMITTED/ESTIMATED);
        // claim payments are already merged into INSURANCE_PAYMENT
        // transactions upstream.
        if (event.kind !== 'transaction') continue;

        const rawAmount = Number(event.amount) || 0;
        const t = (event.transaction_type || '').toUpperCase();
        const sub = (event.transaction_subtype || '').toUpperCase();
        // If the user opted into a single-date override, every transactional
        // line item is stamped with that date so the public invoice mirrors
        // the preview. Otherwise we keep each event's actual date.
        const dateStr =
          invoiceDateOverride ||
          event.transaction_date ||
          event.occurred_at ||
          null;

        if (t === 'CHARGE' || t === 'PROCEDURE') {
          subtotalProcedures += rawAmount;
          lineItems.push({
            description: labelFor(event),
            quantity: 1,
            price: rawAmount,
            code: event.procedure_code || '',
            type: 'PROCEDURE',
            date: dateStr,
            postedTo: event.posted_to_name || null,
          });
        } else if (t === 'ADJUSTMENT') {
          totalAdjustments += rawAmount;
          lineItems.push({
            description: labelFor(event),
            quantity: 1,
            price: rawAmount,
            code: '',
            type: 'ADJUSTMENT',
            date: dateStr,
            postedTo: event.posted_to_name || null,
          });
        } else if (t === 'PAYMENT') {
          // Payments are credits — store them as negative line prices so
          // the line-item math reconciles with the balance due.
          const credit = Math.abs(rawAmount);
          const isInsurance = sub.includes('INSURANCE');
          if (isInsurance) totalInsurancePayments += credit;
          else totalPatientPayments += credit;
          lineItems.push({
            description: labelFor(event),
            quantity: 1,
            price: -credit,
            code: '',
            type: 'PAYMENT',
            isInsurance,
            date: dateStr,
            postedTo: event.posted_to_name || null,
          });
        }
      }

      // Default due date: 7 days out, matching the Transactions tab default.
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      const dueDateStr = dueDate.toISOString().split('T')[0];

      const invoiceData = {
        patientId: invoicePreview.billToContactId || invoicePreview.contact_id,
        patientName: invoicePreview.name,
        patientPhone: invoicePreview.patientPhone || '',
        patientEmail: invoicePreview.patientEmail || '',
        lineItems,
        total: invoicePreview.balanceDue,
        dueDate: dueDateStr,
        description:
          invoicePreview.kind === 'family'
            ? `Statement for ${invoicePreview.billToName} (Family)`
            : `Statement for ${invoicePreview.name}`,
        summary: {
          previousBalance,
          subtotalProcedures: Math.round(subtotalProcedures * 100) / 100,
          totalAdjustments: Math.round(totalAdjustments * 100) / 100,
          totalInsurancePayments: Math.round(totalInsurancePayments * 100) / 100,
          totalPatientPayments: Math.round(totalPatientPayments * 100) / 100,
          netChange: invoicePreview.totals.netChange,
          balanceDue: invoicePreview.balanceDue,
        },
        dateRange: invoicePreview.dateRange,
        sourceView: invoicePreview.kind,
        // When the bill was redirected from a dependent up to their
        // guarantor (Open Dental Family Account model) we record both
        // sides so the public invoice can show "Billed to <Guarantor>
        // on behalf of <Patient>".
        billedToGuarantor: Boolean(invoicePreview.billedToGuarantor),
        onBehalfOfName: invoicePreview.onBehalfOfName || null,
        onBehalfOfContactId: invoicePreview.billedToGuarantor
          ? invoicePreview.contact_id
          : null,
        // Persisted so we can tell on the public invoice / audit log that
        // line-item dates were intentionally collapsed to a single day
        // rather than reflecting actual activity dates.
        dateOverride: invoiceDateOverride || null,
        // Opt in to auto-charging the patient's default card on file.
        // Backend re-validates the practice toggle and the actual card
        // before charging; this flag just signals "I confirmed this in
        // the preview" so the auto-charge isn't accidentally triggered
        // for invoice flows that don't show the checkbox.
        autoChargeRequested: !!autoChargeRequested && !!previewDefaultCard,
      };

      const result = await createInvoice(practiceId, invoiceData, { supersede });
      if (result && result.success) {
        setInvoiceCreated({ success: true, invoice: result.invoice });
        setPendingConflict(null);
        // "Create & Charge": hand the new invoice off to the Invoices tab,
        // which opens the terminal/device charge flow for it. Close the
        // preview so the user lands cleanly on the charge modal. Modal-only
        // callers (e.g. the Schedule tab) charge in place, so also drop the
        // background patient/family modal, as the send-link path does.
        if (chargeAfter && result.invoice && onRequestCharge) {
          closeInvoicePreview();
          if (modalOnly) setSelectedPatient(null);
          onRequestCharge(result.invoice);
        } else if (result.invoice?.publicHash) {
          // Otherwise offer to send the payment link to the patient. The
          // modal exposes SMS / Email / Both — enabled per whichever
          // contact details we have on file for this patient.
          setSendLinkModal({
            invoice: result.invoice,
            patientName:
              invoicePreview.kind === 'family'
                ? invoicePreview.billToName
                : invoicePreview.name,
            phone: invoicePreview.patientPhone || '',
            email: invoicePreview.patientEmail || '',
          });
          setSendFeedback(null);
        }
      } else if (result && result.conflict) {
        // Backend found an existing pending invoice for this patient.
        // Park it in pendingConflict and let the dialog drive the next
        // step (cancel = no-op, confirm = retry with supersede=true).
        // Remember the charge intent so a replace still charges.
        setPendingConflict({
          existing: result.existing_invoice || {},
          existingCount: result.existing_count || 1,
          chargeAfter,
        });
      } else {
        setInvoiceCreated({
          success: false,
          error: (result && (result.error || result.message)) || 'Failed to create invoice',
        });
      }
    } catch (err) {
      console.error('Error creating invoice from ledger:', err);
      setInvoiceCreated({
        success: false,
        error: err?.message || 'Failed to create invoice',
      });
    } finally {
      setCreatingInvoice(false);
    }
  }, [practiceId, invoicePreview, creatingInvoice, invoiceDateOverride, autoChargeRequested, previewDefaultCard, onRequestCharge, closeInvoicePreview, modalOnly]);

  // Send the freshly-created statement's payment link to the patient over
  // one or both channels. `channel` is 'sms' | 'email' | 'both'. Each leg is
  // attempted independently so "Both" still reports partial success (e.g.
  // SMS lands but the email bounces). Results are surfaced inline in the
  // modal; on full success the modal auto-closes after a beat.
  const handleSendLink = useCallback(async (channel) => {
    if (!sendLinkModal || sendingChannel) return;
    const { invoice, phone, email } = sendLinkModal;
    setSendingChannel(channel);
    setSendFeedback(null);
    try {
      const results = [];
      if (channel === 'sms' || channel === 'both') {
        if (!phone) {
          results.push({ ok: false, label: 'SMS', msg: 'no phone on file' });
        } else {
          const r = await sendInvoiceSms(practiceId, invoice.id, phone);
          const ok = !!(r && r.success);
          results.push({ ok, label: 'SMS', msg: r?.error });
          // Stamp the invoice timeline. The send-sms route only records
          // smsNotifications[]; the activityLog (which feeds the Timeline
          // modal) is written separately, mirroring InvoiceTab.
          if (ok) logInvoiceActivity(practiceId, invoice.id, 'sms_sent', { phoneNumber: phone });
        }
      }
      if (channel === 'email' || channel === 'both') {
        if (!email) {
          results.push({ ok: false, label: 'Email', msg: 'no email on file' });
        } else {
          const r = await sendInvoiceEmail(practiceId, invoice.id, email);
          const ok = !!(r && r.success);
          results.push({ ok, label: 'Email', msg: r?.error });
          if (ok) logInvoiceActivity(practiceId, invoice.id, 'email_sent', { email });
        }
      }
      const failed = results.filter((x) => !x.ok);
      if (failed.length === 0) {
        setSendFeedback({
          type: 'success',
          message: `Payment link sent via ${results.map((x) => x.label).join(' + ')}.`,
        });
        setTimeout(() => {
          setSendLinkModal(null);
          setSendFeedback(null);
        }, 1400);
      } else {
        setSendFeedback({
          type: 'error',
          message: failed
            .map((f) => `${f.label} failed${f.msg ? `: ${f.msg}` : ''}`)
            .join('; '),
        });
      }
    } catch (err) {
      console.error('Error sending payment link:', err);
      setSendFeedback({ type: 'error', message: err?.message || 'Send failed' });
    } finally {
      setSendingChannel(null);
    }
  }, [practiceId, sendLinkModal, sendingChannel]);

  // When the user clicked "Invoice" on a Latest Settled row before the
  // ledger had finished loading, this effect fires once the right ledger
  // arrives and pops the preview on top of the patient modal.
  useEffect(() => {
    if (!pendingInvoiceFor) return;
    if (!selectedPatient || pendingInvoiceFor.contactId !== selectedPatient.contact_id) return;
    const wantFamily = pendingInvoiceFor.view === 'family';
    const sourceLedger = wantFamily ? householdLedger : ledger;
    if (!sourceLedger) return;
    // Deep-links (e.g. the AR Aging "Send invoice" button) arrive with only
    // { contact_id, name } — no phone/email — so the "Send payment link"
    // modal would show "None on file" even when contact details exist. The
    // patient-ledger response (`ledger.patient`) carries the head's phone and
    // email; the household ledger's guarantor/members do not. In family view
    // the source ledger is the household one, so wait for the patient ledger
    // too (or an error) and backfill contact details from it before building
    // the preview. This keeps `patientInfo` complete regardless of which
    // ledger resolves first.
    if (!ledger && !ledgerError) return;
    const contactInfo = {
      ...selectedPatient,
      phone: selectedPatient.phone || ledger?.patient?.phone || '',
      email: selectedPatient.email || ledger?.patient?.email || '',
    };
    openInvoicePreview(sourceLedger, pendingInvoiceFor.view, contactInfo, pendingInvoiceFor.range);
    setPendingInvoiceFor(null);
  }, [pendingInvoiceFor, selectedPatient, ledger, ledgerError, householdLedger, openInvoicePreview]);

  const loadLatestSettled = useCallback(async () => {
    if (!practiceId) return;
    setSettledLoading(true);
    setSettledError(null);
    try {
      const data = await getLatestSettled(
        practiceId,
        selectedDate ? { date: selectedDate } : {}
      );
      if (data && data.success) {
        setLatestSettled(data);
      } else {
        setSettledError(data?.error || 'Failed to load latest settled');
        setLatestSettled(null);
      }
    } catch (err) {
      console.error('Latest-settled error:', err);
      setSettledError(err.message || 'Failed to load latest settled');
      setLatestSettled(null);
    } finally {
      setSettledLoading(false);
    }
  }, [practiceId, selectedDate]);

  useEffect(() => {
    loadLatestSettled();
  }, [loadLatestSettled]);

  // Back to page 1 whenever the list itself changes (new data, other day,
  // other filter) so the pager never points past the end.
  useEffect(() => {
    setSettledPage(1);
  }, [latestSettled, settledFilter]);

  // A bulk selection / result only makes sense for the day it was made on.
  useEffect(() => {
    setBulkSelected(new Set());
    setBulkReview(null);
    setBulkResults(null);
    setBulkError(null);
  }, [practiceId, selectedDate]);

  const bulkChannelList = { both: ['sms', 'email'], sms: ['sms'], email: ['email'], none: [] }[bulkChannels];

  const reviewBulk = useCallback(async () => {
    setBulkBusy('review');
    setBulkError(null);
    try {
      const res = await bulkInvoiceFromLedger(practiceId, {
        contactIds: Array.from(bulkSelected),
        channels: bulkChannelList,
        dryRun: true,
      });
      if (!res?.success) throw new Error(res?.error || 'Could not check the selected patients');
      setBulkReview(res);
    } catch (err) {
      setBulkError(err.message || 'Could not check the selected patients');
    } finally {
      setBulkBusy(null);
    }
  }, [practiceId, bulkSelected, bulkChannelList]);

  const runBulk = useCallback(async () => {
    setBulkBusy('send');
    setBulkError(null);
    try {
      const res = await bulkInvoiceFromLedger(practiceId, {
        contactIds: Array.from(bulkSelected),
        channels: bulkChannelList,
        dryRun: false,
      });
      if (!res?.success) throw new Error(res?.error || 'Bulk invoice failed');
      const byContact = {};
      (res.households || []).forEach((h) => {
        (h.contactIds || []).forEach((cid) => { byContact[cid] = h; });
      });
      setBulkResults({ byContact, summary: res.summary, households: res.households || [] });
      setBulkReview(null);
      setBulkSelected(new Set());
    } catch (err) {
      setBulkError(err.message || 'Bulk invoice failed');
    } finally {
      setBulkBusy(null);
    }
  }, [practiceId, bulkSelected, bulkChannelList]);

  // Today, in the user's local time, as YYYY-MM-DD — used as the date
  // picker's max so the user can't choose a future settlement day.
  const todayLocalIso = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  const handleClearPatient = () => {
    clearTimeout(syncReloadTimer.current);
    setSyncNotice(null);
    setSelectedPatient(null);
    setLedger(null);
    setLedgerError(null);
    setSearchQuery('');
  };

  // Open Dental's Account module has two tabs ("Patient" / "Family"). Mirror
  // that here: when the user flips to Family we surface the household
  // payload, otherwise the per-patient payload. We always keep `patient`
  // pointing at the anchor patient (the one the user opened the modal from)
  // so the modal title doesn't shift unexpectedly.
  const isFamilyView = ledgerView === 'family';
  const activeLedger = isFamilyView ? householdLedger : ledger;
  const activeLoading = isFamilyView ? householdLoading : ledgerLoading;
  const activeError = isFamilyView ? householdError : ledgerError;
  // Memoized so dependent hooks (e.g. memberStylesByCid) don't see a fresh
  // array reference on every render and re-run unnecessarily.
  const householdMembers = useMemo(
    () => householdLedger?.members || [],
    [householdLedger]
  );
  const hasHousehold = householdMembers.length > 1
    || !!ledger?.household?.guarantor
    || (ledger?.household?.dependents && ledger.household.dependents.length > 0);

  const filteredEvents = useMemo(() => {
    const events = activeLedger?.events || [];
    if (!events.length) return [];
    if (eventFilter === 'transactions') return events.filter((e) => e.kind === 'transaction');
    if (eventFilter === 'claims') return events.filter((e) => e.kind === 'claim');
    return events;
  }, [activeLedger, eventFilter]);

  const summary = activeLedger?.summary;
  const patient = ledger?.patient;

  // True if the active ledger (patient or family view) has any non-PAID
  // claims still on the books — SUBMITTED, IN_PROCESS, CREATED, etc. We
  // suppress the Create-Invoice CTAs in that case so the practice doesn't
  // bill the patient for amounts insurance is still expected to cover.
  // Mirrors the backend rule (state != 'PAID') used by Latest Settled.
  const hasPendingClaims = useMemo(() => {
    const byState = summary?.claims_by_state || {};
    return Object.entries(byState).some(
      ([state, bucket]) => state !== 'PAID' && (bucket?.count || 0) > 0
    );
  }, [summary]);

  // Stable color per household member so the Initials chip is consistent
  // across the whole timeline (Open Dental uses the same idea).
  const memberStylesByCid = useMemo(() => {
    const palette = [
      { bg: '#dbeafe', fg: '#1d4ed8' }, // blue
      { bg: '#fce7f3', fg: '#be185d' }, // pink
      { bg: '#dcfce7', fg: '#15803d' }, // green
      { bg: '#fef3c7', fg: '#a16207' }, // amber
      { bg: '#ede9fe', fg: '#6d28d9' }, // violet
      { bg: '#cffafe', fg: '#0e7490' }, // cyan
      { bg: '#ffedd5', fg: '#c2410c' }, // orange
    ];
    const out = {};
    householdMembers.forEach((m, i) => {
      out[m.contact_id] = palette[i % palette.length];
    });
    return out;
  }, [householdMembers]);

  return (
    <div style={modalOnly ? { display: 'contents' } : undefined}>
      {/* In modal-only mode we skip all page chrome + the patient/raw modals and
          render just the invoice-preview flow overlays below. */}
      {modalOnly && !selectedPatient && !invoiceFlowOpenedRef.current && (
        <div
          onClick={() => onClose && onClose()}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: 12, padding: '20px 28px',
              fontSize: 14, color: '#3c4540', boxShadow: '0 24px 48px rgba(15,23,42,0.3)',
            }}
          >
            Opening invoice…
          </div>
        </div>
      )}
      {!modalOnly && (
        <>
      {/* Patient search — searches every patient in the PMS, not just the
          settled list below, so it stays above the KPI strip. */}
      <div ref={searchBoxRef} style={{ position: 'relative', marginBottom: 16 }}>
        <div
          className="pm-search"
          style={{ boxShadow: showResults ? '0 4px 16px rgba(0,0,0,0.08)' : undefined }}
        >
          <Icon name="search" size={16} />
          <input
            type="text"
            value={searchQuery}
            placeholder="Search patients by name, phone, email, or contact ID…"
            aria-label="Search patients"
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (selectedPatient) setSelectedPatient(null);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
          />
          {selectedPatient && (
            <button type="button" className="pm-search-clear" onClick={handleClearPatient}>
              Clear
            </button>
          )}
        </div>

        {showResults && (
          <div
                        style={{
                          position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: 4,
              backgroundColor: '#fff',
              border: '1px solid #e3e7e4',
              borderRadius: 8,
              maxHeight: 380,
              overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              zIndex: 20,
            }}
          >
            {searchLoading && (
              <div style={{ padding: 16, color: '#6b716d', fontSize: 14 }}>Searching…</div>
            )}
            {!searchLoading && searchError && (
              <div style={{ padding: 16, color: '#b42318', fontSize: 14 }}>{searchError}</div>
            )}
            {!searchLoading && !searchError && searchResults.length === 0 && (
              <div style={{ padding: 16, color: '#6b716d', fontSize: 14 }}>
                {searchQuery ? 'No patients found.' : 'Start typing to search…'}
                  </div>
            )}
            {!searchLoading &&
              searchResults.map((p) => (
                <button
                  key={p.contact_id}
                  onClick={() => handleSelectPatient(p)}
                        style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: '1px solid #eef0ee',
                    background: 'transparent',
                    textAlign: 'left',
                          cursor: 'pointer',
                        }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7f8f7')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                  <div>
                    <div data-phi style={{ fontSize: 14, fontWeight: 600, color: '#1a1d1b' }}>{p.name}</div>
                    <div data-phi style={{ fontSize: 12, color: '#6b716d', marginTop: 2 }}>
                      {[formatPhone(p.phone), p.email].filter(Boolean).join(' · ') || `Contact #${p.contact_id}`}
                    </div>
                        </div>
                  <div data-phi style={{ fontSize: 11, color: '#a3a8a4' }}>#{p.contact_id}</div>
                          </button>
                      ))}
                        </div>
        )}
                    </div>
                    
      {/* Latest Settled — KPI strip, then one card with toolbar, table and pager. */}
      {(() => {
        const totals = latestSettled?.totals;
        const allPatients = latestSettled?.patients || [];
        const visiblePatients = allPatients.filter((p) => {
          if (settledFilter === 'owes') return p.patient_balance > 0;
          if (settledFilter === 'settled') return p.patient_balance <= 0;
          return true;
        });
        const owesCount = allPatients.filter((p) => p.patient_balance > 0).length;
        const settledCount = allPatients.length - owesCount;

        const pageCount = Math.max(1, Math.ceil(visiblePatients.length / SETTLED_PAGE_SIZE));
        const page = Math.min(settledPage, pageCount);
        const pageStart = (page - 1) * SETTLED_PAGE_SIZE;
        const pagePatients = visiblePatients.slice(pageStart, pageStart + SETTLED_PAGE_SIZE);

        const showRows = !settledLoading && visiblePatients.length > 0;

        // Bulk selection follows the rule that shows a row's Invoice button.
        const BULK_MAX = 50;
        const rowBalance = (p) => (p.has_family && p.family ? p.family.patient_balance : p.patient_balance);
        const isInvoiceable = (p) => {
          const pending = p.has_family && p.family ? p.family.pending_claims_count : p.pending_claims_count;
          return rowBalance(p) > 0 && pending === 0;
        };
        const bulkResultFor = (p) => bulkResults?.byContact?.[p.contact_id] || null;
        const bulkTarget = visiblePatients
          .filter((p) => isInvoiceable(p) && !bulkResultFor(p))
          .slice(0, BULK_MAX);
        const allBulkSelected = bulkTarget.length > 0 && bulkTarget.every((p) => bulkSelected.has(p.contact_id));
        const someBulkSelected = bulkTarget.some((p) => bulkSelected.has(p.contact_id));
        const toggleAllBulk = () =>
          setBulkSelected(allBulkSelected ? new Set() : new Set(bulkTarget.map((p) => p.contact_id)));
        const toggleBulk = (cid) =>
          setBulkSelected((prev) => {
            const next = new Set(prev);
            if (next.has(cid)) next.delete(cid);
            else if (next.size < BULK_MAX) next.add(cid);
            return next;
          });
        // Selected total, counting a household once even if two members are ticked.
        const bulkHouseholds = new Map();
        allPatients.forEach((p) => {
          if (!bulkSelected.has(p.contact_id)) return;
          bulkHouseholds.set(p.has_family ? p.household_head_id : p.contact_id, rowBalance(p));
        });
        const bulkSelectedTotal = Array.from(bulkHouseholds.values()).reduce((a, b) => a + b, 0);
        const bulkChip = (h) => {
          if (h.status === 'created') {
            const sent = Object.entries(h.sent || {});
            const ok = sent.filter(([, v]) => v.ok).map(([k]) => (k === 'sms' ? 'text' : 'email'));
            const failed = sent.filter(([, v]) => !v.ok);
            const title = [
              `Invoice ${h.invoiceId} created`,
              ok.length ? `sent by ${ok.join(' and ')}` : null,
              ...failed.map(([k, v]) => `${k === 'sms' ? 'Text' : 'Email'} not sent: ${v.error}`),
            ].filter(Boolean).join(' · ');
            return (
              <span className={`pm-tag ${failed.length ? 'pm-tag-warn' : 'pm-tag-ok'}`} title={title}>
                {ok.length ? 'Sent' : 'Invoiced'}
                {failed.length ? (ok.length ? ' · partial' : ' · not sent') : ''}
              </span>
            );
          }
          if (h.status === 'skip') {
            return <span className="pm-tag pm-tag-muted" title={h.reasonLabel || h.reason}>Skipped</span>;
          }
          return <span className="pm-tag pm-tag-danger" title={h.error || 'Failed'}>Failed</span>;
        };
        const bulkCounts = bulkResults
          ? bulkResults.households.reduce(
              (acc, h) => {
                if (h.status === 'created') acc.created += 1;
                if (h.status === 'skip') acc.skipped += 1;
                if (h.status === 'failed') acc.failed += 1;
                if (h.sent?.sms?.ok) acc.texts += 1;
                if (h.sent?.email?.ok) acc.emails += 1;
                return acc;
              },
              { created: 0, skipped: 0, failed: 0, texts: 0, emails: 0 }
            )
          : null;

        const tz = describeTimezone(
          latestSettled?.timezone,
          selectedDate || latestSettled?.settlement_date
        );

        return (
          <>
            {!settledCollapsed && (
              <div className="pm-kpis">
                <KpiTile
                  label="Patients settled"
                  value={totals ? String(totals.patient_count) : '—'}
                  sub={
                    totals
                      ? `${totals.claims_count} claim${totals.claims_count === 1 ? '' : 's'}${
                          totals.family_count > 0
                            ? ` · ${totals.household_count} household${
                                totals.household_count === 1 ? '' : 's'
                              }`
                            : ''
                        }`
                      : null
                  }
                />
                <KpiTile
                  label="Insurance paid"
                  value={totals ? formatCurrency(totals.paid_today) : '—'}
                  valueColor="var(--pm-ok)"
                  sub="Settled by carriers"
                />
                <KpiTile
                  label="Outstanding insurance"
                  value={totals ? formatCurrency(totals.outstanding_insurance || 0) : '—'}
                  valueColor={totals?.outstanding_insurance > 0 ? 'var(--pm-warn)' : undefined}
                  sub={totals && totals.pending_claims_count > 0
                    ? `${totals.pending_claims_count} pending claim${totals.pending_claims_count === 1 ? '' : 's'}${
                        totals.family_count > 0 ? ' · household' : ''
                      }`
                    : 'No pending claims'}
                />
                <KpiTile
                  label="Households who owe"
                  value={
                    totals
                      ? String(
                          totals.households_with_balance != null
                            ? totals.households_with_balance
                            : totals.patients_with_balance
                        )
                      : '—'
                  }
                  valueColor={
                    (totals?.households_with_balance ?? totals?.patients_with_balance ?? 0) > 0
                      ? 'var(--pm-danger)'
                      : undefined
                  }
                  sub={
                    totals && totals.household_count > 0
                      ? `${Math.round(
                          ((totals.households_with_balance ?? totals.patients_with_balance ?? 0) /
                            totals.household_count) *
                            100
                        )}% of ${totals.household_count} settled${
                          totals.pending_claims_count > 0 ? ' · invoiceable today' : ''
                        }`
                      : null
                  }
                />
                <KpiTile
                  label="Balance due"
                  value={totals ? formatCurrency(totals.outstanding_patient_balance) : '—'}
                  valueColor={totals?.outstanding_patient_balance > 0 ? 'var(--pm-danger)' : undefined}
                  sub={
                    totals && totals.pending_claims_count > 0
                      ? `Excludes households w/ pending claims${
                          totals.family_count > 0
                            ? ` · ${totals.family_count} famil${totals.family_count === 1 ? 'y' : 'ies'}`
                            : ''
                        }`
                      : totals && totals.family_count > 0
                      ? `Net per household · ${totals.family_count} famil${
                          totals.family_count === 1 ? 'y' : 'ies'
                        }`
                      : 'Across all patients above'
                  }
                />
              </div>
            )}

            <section className="pm-card" aria-label={selectedDate ? 'Settled' : 'Latest settled'}>
              {/* Toolbar */}
              <div className="pm-toolbar">
                <DatePopover
                  value={selectedDate || latestSettled?.settlement_date || null}
                  max={todayLocalIso}
                  isLatest={!selectedDate}
                  disabled={settledLoading}
                  onChange={(iso) => setSelectedDate(iso)}
                  onSelectLatest={() => setSelectedDate(null)}
                />
                {!settledCollapsed && allPatients.length > 0 && (
                  <div className="pm-segmented" role="group" aria-label="Filter patients">
                    {[
                      { id: 'all', label: 'All', count: allPatients.length },
                      { id: 'owes', label: 'Owes practice', count: owesCount },
                      { id: 'settled', label: 'Settled', count: settledCount },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className="pm-segment"
                        aria-pressed={settledFilter === opt.id}
                        onClick={() => setSettledFilter(opt.id)}
                      >
                        {opt.label}
                        <span className="pm-segment-count">{opt.count}</span>
                      </button>
                    ))}
                  </div>
                )}
                <span
                  className="pm-toolbar-meta"
                  title={tz ? `Times shown in ${tz.long} (${latestSettled.timezone})` : undefined}
                >
                  {selectedDate ? 'Claims marked paid this day' : 'Claims most recently marked paid'}
                  {tz ? ` · ${tz.short}` : ''}
                </span>
                <button
                  type="button"
                  className="pm-icon-btn"
                  onClick={loadLatestSettled}
                  disabled={settledLoading}
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <Icon name="refresh" size={16} className={settledLoading ? 'pm-spin' : undefined} />
                </button>
                <button
                  type="button"
                  className="pm-icon-btn"
                  onClick={() => setSettledCollapsed((v) => !v)}
                  title={settledCollapsed ? 'Show settled patients' : 'Hide settled patients'}
                  aria-label={settledCollapsed ? 'Show settled patients' : 'Hide settled patients'}
                  aria-expanded={!settledCollapsed}
                >
                  <Icon name={settledCollapsed ? 'chevron-down' : 'chevron-up'} size={16} />
                </button>
              </div>

              {!settledCollapsed && (
                <>
                  {bulkSelected.size > 0 && (
                    <div className="pm-toolbar pm-bulkbar">
                      <span className="pm-bulkbar-count">
                        {bulkSelected.size} selected · {formatCurrency(bulkSelectedTotal)}
                      </span>
                      <div className="pm-segmented" role="group" aria-label="Send invoices by">
                        {[
                          ['both', 'Text + email'],
                          ['sms', 'Text'],
                          ['email', 'Email'],
                          ['none', 'Create only'],
                        ].map(([id, label]) => (
                          <button
                            key={id}
                            type="button"
                            className="pm-segment"
                            aria-pressed={bulkChannels === id}
                            onClick={() => setBulkChannels(id)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="pm-btn pm-btn-sm"
                        onClick={() => setBulkSelected(new Set())}
                        disabled={!!bulkBusy}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="pm-btn-primary pm-btn-sm pm-toolbar-end"
                        onClick={reviewBulk}
                        disabled={!!bulkBusy}
                      >
                        <Icon name="send" size={14} strokeWidth={2} />
                        {bulkBusy === 'review' ? 'Checking…' : 'Review & send'}
                      </button>
                    </div>
                  )}
                  {bulkError && !bulkReview && (
                    <div className="pm-empty pm-empty-error">{bulkError}</div>
                  )}
                  {bulkCounts && (
                    <div className="pm-toolbar pm-bulkbar">
                      <span className="pm-bulkbar-count">
                        {bulkCounts.created} invoice{bulkCounts.created === 1 ? '' : 's'} created
                        {` · ${bulkCounts.texts} text${bulkCounts.texts === 1 ? '' : 's'}`}
                        {` · ${bulkCounts.emails} email${bulkCounts.emails === 1 ? '' : 's'} sent`}
                        {bulkCounts.skipped > 0 && ` · ${bulkCounts.skipped} skipped`}
                        {bulkCounts.failed > 0 && ` · ${bulkCounts.failed} failed`}
                      </span>
                      <button
                        type="button"
                        className="pm-btn pm-btn-sm pm-toolbar-end"
                        onClick={() => setBulkResults(null)}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  {settledLoading && <div className="pm-empty">Loading…</div>}
                  {!settledLoading && settledError && (
                    <div className="pm-empty pm-empty-error">{settledError}</div>
                  )}
                  {!settledLoading && !settledError && allPatients.length === 0 && (
                    <div className="pm-empty">
                      <div className="pm-empty-title">
                        {selectedDate ? 'No claims settled this day' : 'No settled claims yet'}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        {selectedDate
                          ? 'No insurance claims were marked PAID on this day.'
                          : 'Check back after carriers post EOBs.'}
                      </div>
                    </div>
                  )}
                  {!settledLoading && !settledError && allPatients.length > 0 && visiblePatients.length === 0 && (
                    <div className="pm-empty">No patients match this filter.</div>
                  )}

                  {showRows && (
                    <div className="pm-table-scroll">
                      <div
                        className="pm-table"
                        style={{
                          '--pm-cols':
                            '28px minmax(200px, 1.5fr) minmax(170px, 1.2fr) minmax(130px, 0.9fr) minmax(110px, 0.8fr) minmax(140px, 1fr) minmax(120px, 0.9fr) 104px',
                          minWidth: 990,
                        }}
                      >
                        <div className="pm-row pm-row-head">
                          <div>
                            {bulkTarget.length > 0 && (
                              <input
                                type="checkbox"
                                className="pm-checkbox"
                                checked={allBulkSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = someBulkSelected && !allBulkSelected;
                                }}
                                onChange={toggleAllBulk}
                                aria-label={`Select all ${bulkTarget.length} patients that can be invoiced`}
                                title={`Select all ${bulkTarget.length} patients that can be invoiced${
                                  bulkTarget.length === BULK_MAX ? ` (max ${BULK_MAX} per run)` : ''
                                }`}
                              />
                            )}
                          </div>
                          <div>Patient</div>
                          <div>Contact</div>
                          <div>Settled</div>
                          <div className="pm-num">Ins. paid</div>
                          <div className="pm-num">Outstanding ins.</div>
                          <div className="pm-num">Balance</div>
                          <div />
                        </div>

                        {pagePatients.map((p) => {
                          // For multi-person households, surface the FAMILY
                          // figures in the three amount columns (Insurance
                          // Paid, Outstanding Insurance, Balance) — i.e. what
                          // an Open Dental Family Account would total. The
                          // patient metadata (claim count today, carrier,
                          // settled-at timestamp) is unchanged.
                          const fam = p.has_family ? p.family || null : null;
                          const isFamilyView = !!fam;
                          const paidValue = isFamilyView ? fam.paid_today : p.paid_today;
                          const outstandingValue = isFamilyView
                            ? fam.outstanding_insurance
                            : p.outstanding_insurance || 0;
                          const pendingCountValue = isFamilyView
                            ? fam.pending_claims_count
                            : p.pending_claims_count;
                          const balanceValue = isFamilyView
                            ? fam.patient_balance
                            : p.patient_balance;
                          const owes = balanceValue > 0;
                          const credit = balanceValue < 0;
                          const carriers = Array.from(
                            new Set((p.claims || []).map((c) => c.carrier))
                          ).filter(Boolean);
                          const carrierDisplay =
                            carriers.length === 0
                              ? '—'
                              : carriers.length <= 2
                              ? carriers.join(', ')
                              : `${carriers[0]}, ${carriers[1]} +${carriers.length - 2}`;
                          const contactLines = [formatPhone(p.phone), p.email].filter(Boolean);

                          const selectThisPatient = (extra) =>
                            handleSelectPatient(
                              {
                                contact_id: p.contact_id,
                                name: p.name,
                                phone: p.phone,
                                email: p.email,
                                settled_at: p.last_paid_at,
                                settled_paid: p.paid_today,
                                settled_claims: p.claims_paid_today,
                              },
                              { ...(p.has_family ? { defaultView: 'family' } : {}), ...(extra || {}) }
                            );

                          return (
                            <div
                              key={p.contact_id}
                              role="button"
                              tabIndex={0}
                              className="pm-row pm-row-body"
                              onClick={() => selectThisPatient()}
                              onKeyDown={(e) => {
                                // Only when the row itself has focus — Enter on
                                // the Invoice button must invoice, not open.
                                if (e.target !== e.currentTarget) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  selectThisPatient();
                                }
                              }}
                            >
                              {/* Bulk select (rows that can be invoiced) */}
                              <div className="pm-cell" onClick={(e) => e.stopPropagation()}>
                                {isInvoiceable(p) && !bulkResultFor(p) && (
                                  <input
                                    type="checkbox"
                                    className="pm-checkbox"
                                    checked={bulkSelected.has(p.contact_id)}
                                    onChange={() => toggleBulk(p.contact_id)}
                                    disabled={!bulkSelected.has(p.contact_id) && bulkSelected.size >= BULK_MAX}
                                    aria-label={`Select ${p.name} for bulk invoice`}
                                  />
                                )}
                              </div>

                              {/* Patient */}
                              <div className="pm-cell">
                                <div className="pm-cell-title-line">
                                  <span className="pm-cell-title" data-phi>{p.name}</span>
                                  {p.has_family && (
                                    <span
                                      className="pm-tag pm-tag-violet"
                                      title={
                                        p.is_guarantor
                                          ? `Guarantor for a ${p.family_size}-person household — open to view family ledger`
                                          : `Member of a ${p.family_size}-person household — open to view family ledger`
                                      }
                                    >
                                      Family{p.family_size > 0 ? ` · ${p.family_size}` : ''}
                                    </span>
                                  )}
                                </div>
                                <div className="pm-cell-sub">
                                  {carrierDisplay} · {p.claims_paid_today} claim
                                  {p.claims_paid_today === 1 ? '' : 's'}
                                </div>
                              </div>

                              {/* Contact */}
                              <div className="pm-cell">
                                {contactLines.length > 0 ? (
                                  <>
                                    <div className="pm-cell-text" data-phi>{contactLines[0]}</div>
                                    {contactLines[1] && (
                                      <div className="pm-cell-sub" data-phi title={contactLines[1]}>{contactLines[1]}</div>
                                    )}
                                  </>
                                ) : (
                                  <div className="pm-cell-text" data-phi>Contact #{p.contact_id}</div>
                                )}
                              </div>

                              {/* Settled */}
                              <div className="pm-cell">
                                {p.last_paid_at ? (
                                  <>
                                    <div
                                      className="pm-cell-text"
                                      title={`Most recent claim marked PAID at ${formatDateTime(p.last_paid_at)}`}
                                    >
                                      {formatSettledAt(p.last_paid_at)}
                                    </div>
                                    <span className="pm-pill pm-pill-ok">Settled</span>
                                  </>
                                ) : (
                                  <div className="pm-cell-sub">—</div>
                                )}
                              </div>

                              {/* Insurance paid */}
                              <div
                                className="pm-cell pm-num"
                                title={
                                  isFamilyView
                                    ? `Sum of insurance paid today across ${fam.member_count} household members`
                                    : undefined
                                }
                              >
                                <div className="pm-amount pm-text-ok">{formatCurrency(paidValue)}</div>
                                <div className="pm-cell-sub">{isFamilyView ? 'Family total' : 'Patient'}</div>
                              </div>

                              {/* Outstanding insurance */}
                              <div
                                className="pm-cell pm-num"
                                title={
                                  isFamilyView
                                    ? 'Carrier still owes the household this much across all members'
                                    : undefined
                                }
                              >
                                <div className={`pm-amount${outstandingValue > 0 ? ' pm-text-warn' : ''}`}>
                                  {formatCurrency(outstandingValue)}
                                </div>
                                <div className="pm-cell-sub">
                                  {pendingCountValue > 0
                                    ? `${pendingCountValue} pending claim${pendingCountValue === 1 ? '' : 's'}${
                                        isFamilyView ? ' · family' : ''
                                      }`
                                    : 'No pending claims'}
                                </div>
                              </div>

                              {/* Balance */}
                              <div
                                className="pm-cell pm-num"
                                title={
                                  isFamilyView
                                    ? `Net household balance across ${fam.member_count} members (Open Dental's Family Balance)`
                                    : undefined
                                }
                              >
                                <div
                                  className={`pm-amount${owes ? ' pm-text-danger' : credit ? ' pm-text-ok' : ''}`}
                                >
                                  {formatCurrency(balanceValue)}
                                </div>
                                <div className={`pm-cell-sub${owes ? ' pm-text-danger' : credit ? ' pm-text-ok' : ''}`}>
                                  {owes
                                    ? isFamilyView ? 'Family owes' : 'Owes practice'
                                    : credit
                                    ? 'Credit on file'
                                    : 'Settled'}
                                </div>
                              </div>

                              {/* Action: Invoice when the balance is billable now, else a chevron */}
                              <div className="pm-cell-actions">
                                {bulkResultFor(p) ? (
                                  bulkChip(bulkResultFor(p))
                                ) : owes && pendingCountValue === 0 ? (
                                  <button
                                    type="button"
                                    className="pm-btn-primary pm-btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      selectThisPatient({ autoInvoice: true });
                                    }}
                                    title={
                                      isFamilyView
                                        ? `Create an invoice from this family's last 2 days of activity (Family Balance ${formatCurrency(
                                            balanceValue
                                          )})`
                                        : `Create an invoice from this patient's last 2 days of activity (Balance ${formatCurrency(
                                            balanceValue
                                          )})`
                                    }
                                  >
                                    <Icon name="send" size={14} strokeWidth={2} />
                                    Invoice
                                  </button>
                                ) : (
                                  <Icon name="chevron-right" size={16} className="pm-row-chevron" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {showRows && (
                    <div className="pm-card-footer">
                      <span>
                        Showing {pageStart + 1}–{pageStart + pagePatients.length} of {visiblePatients.length}
                      </span>
                      <Pager
                        page={page}
                        pageCount={pageCount}
                        onChange={setSettledPage}
                        label="Settled patients pages"
                      />
                    </div>
                  )}
                </>
              )}
            </section>

            {bulkReview && (() => {
              const s = bulkReview.summary || {};
              const households = bulkReview.households || [];
              const ready = households.filter((h) => h.status === 'ready');
              const notReady = households.filter((h) => h.status !== 'ready');
              const wantsSms = bulkChannelList.includes('sms');
              const wantsEmail = bulkChannelList.includes('email');
              const channelLine = [
                wantsSms && `${s.withPhone} text${s.withPhone === 1 ? '' : 's'}`,
                wantsEmail && `${s.withEmail} email${s.withEmail === 1 ? '' : 's'}`,
              ].filter(Boolean).join(' · ') || 'Invoices only, nothing sent';
              const close = () => {
                if (bulkBusy) return;
                setBulkReview(null);
                setBulkError(null);
              };
              return (
                <div className="pm-gr-dialog-backdrop" onClick={close}>
                  <div
                    className="pm-gr-dialog pm-bulk-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Review bulk invoice"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className="pm-gr-dialog-title">
                      Invoice {s.ready} household{s.ready === 1 ? '' : 's'} · {formatCurrency(s.total || 0)}
                    </h3>
                    <p className="pm-gr-dialog-sub">
                      {channelLine}. One statement per household, billed to the guarantor, due in 7 days.
                    </p>
                    {wantsSms && s.ready - s.withPhone > 0 && (
                      <p className="pm-gr-dialog-sub">
                        {s.ready - s.withPhone} without a phone number won't get a text.
                      </p>
                    )}
                    {wantsEmail && s.ready - s.withEmail > 0 && (
                      <p className="pm-gr-dialog-sub">
                        {s.ready - s.withEmail} without an email won't get an email.
                      </p>
                    )}
                    {ready.length > 0 && (
                      <ul className="pm-bulk-list">
                        {ready.map((h) => (
                          <li key={h.anchorContactId}>
                            <span data-phi>{h.name}</span>
                            <span className="pm-bulk-list-meta">
                              {wantsSms && !h.hasPhone && <span className="pm-tag pm-tag-muted">No phone</span>}
                              {wantsEmail && !h.hasEmail && <span className="pm-tag pm-tag-muted">No email</span>}
                              <span className="pm-amount">{formatCurrency(h.total)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {notReady.length > 0 && (
                      <>
                        <div className="pm-bulk-list-title">Skipped ({notReady.length})</div>
                        <ul className="pm-bulk-list">
                          {notReady.map((h) => (
                            <li key={h.anchorContactId}>
                              <span data-phi>{h.name || `Contact #${h.anchorContactId}`}</span>
                              <span className="pm-bulk-list-meta pm-gr-muted">
                                {h.reasonLabel || h.error || 'Could not check'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {bulkError && <div className="pm-gr-error">{bulkError}</div>}
                    <div className="pm-gr-dialog-actions">
                      <button type="button" className="pm-btn" onClick={close} disabled={!!bulkBusy}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="pm-btn-primary"
                        onClick={runBulk}
                        disabled={!!bulkBusy || !s.ready}
                      >
                        {bulkBusy === 'send'
                          ? 'Sending…'
                          : bulkChannelList.length
                          ? `Create & send ${s.ready} invoice${s.ready === 1 ? '' : 's'}`
                          : `Create ${s.ready} invoice${s.ready === 1 ? '' : 's'}`}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        );
      })()}
        </>
      )}

      {/* Patient detail modal. In modal-only mode this is the background modal
          the invoice preview stacks on top of — the same dual-popup staff see
          in the Ledger tab. */}
      {patientModalOpen && (
        <div
          onClick={handleClearPatient}
                                style={{
          position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
          display: 'flex',
            alignItems: 'flex-start',
          justifyContent: 'center',
            zIndex: 60,
            padding: '32px 16px',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
                style={{
              backgroundColor: '#f7f8f7',
              borderRadius: 12,
              width: '100%',
              maxWidth: 1280,
              maxHeight: 'calc(100vh - 64px)',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <style>{'@keyframes newtonSpin { to { transform: rotate(360deg); } }'}</style>
            {/* Modal header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: '1px solid #e3e7e4',
                backgroundColor: '#fff',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    backgroundColor: colorForName(selectedPatient?.name),
                    color: colorForNameDark(selectedPatient?.name),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                  data-phi
                >
                  {initialsFor(selectedPatient?.name)}
                                </div>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span data-phi style={{ fontSize: 16, fontWeight: 700, color: '#16241c', lineHeight: 1.2 }}>
                      {patient?.name || selectedPatient?.name || 'Patient'}
                                  </span>
                    {selectedPatient?.settled_at && (
                      <span
                style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#1d6b40',
                          backgroundColor: '#e7f4ec',
                          borderRadius: 999,
                          padding: '2px 8px',
                          letterSpacing: 0.2,
                        }}
                        title={
                          selectedPatient.settled_paid != null
                            ? `Insurance paid ${formatCurrency(selectedPatient.settled_paid)} on ${selectedPatient.settled_claims} claim${
                                selectedPatient.settled_claims === 1 ? '' : 's'
                              }`
                            : undefined
                        }
                      >
                        ✓ Settled · {formatDateTime(selectedPatient.settled_at)}
                      </span>
                    )}
                                </div>
                  <div style={{ fontSize: 12, color: '#6b716d', marginTop: 2 }}>
                    Patient ledger · combined transactions and insurance claims
                                </div>
                                </div>
                                </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <button
                  onClick={handleSyncFromPMS}
                  disabled={syncing}
                  title={
                    lastSyncedAt
                      ? `Last synced ${formatDateTime(new Date(lastSyncedAt).toISOString())}`
                      : 'Pull the latest transactions and claims from the practice management system'
                  }
                  style={{
                    background: syncing ? '#eef0ee' : '#fff',
                    border: '1px solid #cdd3cf',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: syncing ? '#8a908c' : '#0f766e',
                    cursor: syncing ? 'default' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 14,
                      lineHeight: 1,
                      display: 'inline-block',
                      animation: syncing ? 'newtonSpin 0.8s linear infinite' : 'none',
                    }}
                  >
                    ⟳
                  </span>
                  {syncing ? 'Syncing…' : 'Sync from PMS'}
                </button>
                <button
                  onClick={handleClearPatient}
                  title="Close (Esc)"
                  style={{
                    background: '#fff',
                    border: '1px solid #f5c6c1',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 13,
                    color: '#b42318',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>×</span> Close
                </button>
              </div>
                                </div>

            {/* Modal body (scrollable) */}
            <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              {syncNotice && !syncError && (
                <div
                  role="status"
                  style={{
                    backgroundColor: '#e8f0f7',
                    color: '#1f4e79',
                    border: '1px solid #c5d8ea',
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: 16,
                    fontSize: 13,
                  }}
                >
                  {syncNotice}
                </div>
              )}
              {syncError && (
                <div
                  style={{
                    backgroundColor: '#fef9f1',
                    color: '#a8520c',
                    border: '1px solid #f3d9ae',
                    borderRadius: 8,
                    padding: '10px 14px',
                    marginBottom: 16,
                    fontSize: 13,
                  }}
                >
                  {syncError}
                </div>
              )}

              {activeLoading && (
                <div style={{ padding: 24, color: '#6b716d', textAlign: 'center' }}>
                  Loading {isFamilyView ? 'family' : 'patient'} ledger…
                        </div>
                      )}

              {!activeLoading && activeError && (
                <div
                      style={{
                    backgroundColor: '#fef5f4',
                    color: '#912018',
                    border: '1px solid #f5c6c1',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 16,
                  }}
                >
                  {activeError}
                        </div>
                      )}

              {!activeLoading && activeLedger && patient && summary && (
                      <div>
                  {/* Patient header */}
                  <div
                    style={{
                      backgroundColor: '#fff',
                      border: '1px solid #e3e7e4',
                      borderRadius: 8,
                      padding: '16px 20px',
                      marginBottom: 16,
              display: 'flex',
              justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
              <div>
                      <div data-phi style={{ fontSize: 20, fontWeight: 600, color: '#1a1d1b' }}>{patient.name}</div>
                      <div data-phi style={{ fontSize: 13, color: '#6b716d', marginTop: 4 }}>
                        Contact #{patient.contact_id}
                        {patient.phone && <> · {formatPhone(patient.phone)}</>}
                        {patient.email && <> · {patient.email}</>}
                      </div>

                      {/* Patient / Family tabs — same idea as Open Dental's
                          Account module ("Patient" vs "Family" tabs above the
                          ledger). We only render the toggle when there's an
                          actual household to switch into. */}
                      {hasHousehold && (
                        <div
                          style={{
                            marginTop: 12,
                            display: 'inline-flex',
                            border: '1px solid #d6dbd7',
                            borderRadius: 8,
                            overflow: 'hidden',
                            backgroundColor: '#fff',
                          }}
                          role="tablist"
                          aria-label="Account view"
                        >
                          {[
                            { id: 'family', label: 'Family' },
                            { id: 'patient', label: 'Patient' },
                          ].map((tab, idx, arr) => {
                            const active = ledgerView === tab.id;
                            const isLast = idx === arr.length - 1;
                            return (
                        <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setLedgerView(tab.id)}
                          style={{
                                  padding: '6px 14px',
                                  fontSize: 13,
                                  fontWeight: 500,
                                  border: 'none',
                                  borderRight: isLast ? 'none' : '1px solid #d6dbd7',
                            cursor: 'pointer',
                                  backgroundColor: active ? '#1f5a3d' : '#fff',
                                  color: active ? '#fff' : '#3c4540',
                          }}
                        >
                                {tab.label}
                        </button>
                  );
                })}
              </div>
            )}

                      {/* In Family view show every household member as a chip
                          (clickable to flip the modal to that patient).
                          In Patient view show the lightweight guarantor /
                          dependents links from the patient endpoint. */}
                      {isFamilyView && householdMembers.length > 0 && (
                        <div
                          style={{
                            marginTop: 10,
          display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
          alignItems: 'center',
                          }}
                        >
                          <span style={{ fontSize: 12, color: '#6b716d' }}>
                            Family of <span data-phi>{householdLedger?.guarantor?.name || 'household'}</span>:
                          </span>
                          {householdMembers.map((m) => {
                            const palette = memberStylesByCid[m.contact_id] || { bg: '#eef0ee', fg: '#16241c' };
                            const isAnchor = m.contact_id === patient.contact_id;
                            return (
              <button
                                key={m.contact_id}
                                type="button"
                                onClick={() => {
                                  setLedgerView('patient');
                                  handleSelectPatient({ contact_id: m.contact_id, name: m.name });
                                }}
                                title={`Open ${m.name}'s patient ledger`}
                style={{
                                  fontSize: 12,
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  border: `1px solid ${isAnchor ? '#1f5a3d' : '#e3e7e4'}`,
                                  backgroundColor: palette.bg,
                                  color: palette.fg,
                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  fontWeight: 500,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    backgroundColor: '#fff',
                                    color: palette.fg,
                                    padding: '1px 5px',
                                    borderRadius: 4,
                                  }}
                                >
                                  {m.initials}
                                </span>
                                <span data-phi>{m.name}</span>
                                {m.is_guarantor && (
                                  <span style={{ fontSize: 9, opacity: 0.7, textTransform: 'uppercase' }}>
                                    Guarantor
                                  </span>
                                )}
              </button>
                            );
                          })}
            </div>
                      )}

                      {!isFamilyView && ledger?.household && (ledger.household.guarantor
                        || (ledger.household.dependents && ledger.household.dependents.length > 0)) && (
                        <div
                          style={{
                            marginTop: 10,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                            alignItems: 'center',
                          }}
                        >
                          {ledger.household.guarantor && (
                            <button
                              type="button"
                              onClick={() =>
                                handleSelectPatient({
                                  contact_id: ledger.household.guarantor.contact_id,
                                  name: ledger.household.guarantor.name,
                                })
                              }
                              title="Open this patient's guarantor (head of household). Patient/personal payments often live on the guarantor's ledger in Dentrix."
                style={{
                                fontSize: 12,
                                padding: '4px 10px',
                                borderRadius: 999,
                                border: '1px solid #ddd2f3',
                                backgroundColor: '#f1ecfb',
                                color: '#5b3fa8',
                  cursor: 'pointer',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <span style={{ opacity: 0.8 }}>Guarantor:</span>
                              <span data-phi>{ledger.household.guarantor.name}</span>
                              <span style={{ opacity: 0.6 }}>→</span>
              </button>
                          )}
                          {ledger.household.dependents && ledger.household.dependents.length > 0 && (
                            <>
                              <span style={{ fontSize: 12, color: '#6b716d' }}>
                                {ledger.household.guarantor ? 'Other in household:' : 'Guarantor of:'}
                              </span>
                              {ledger.household.dependents.map((d) => (
              <button
                                  key={d.contact_id}
                                  type="button"
                                  onClick={() =>
                                    handleSelectPatient({
                                      contact_id: d.contact_id,
                                      name: d.name,
                                    })
                                  }
                                  title={`Open ${d.name}'s ledger`}
                style={{
                                    fontSize: 12,
                                    padding: '4px 10px',
                                    borderRadius: 999,
                                    border: '1px solid #e3e7e4',
                                    backgroundColor: '#f7f8f7',
                                    color: '#16241c',
                  cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                }}
              >
                                  <span data-phi>{d.name}</span>
                                  <span style={{ opacity: 0.6 }}>→</span>
              </button>
                              ))}
                            </>
                          )}
            </div>
                      )}
          </div>
                    {/* Right side of header: Create Invoice button (when
                        owed) sits on the same row as the balance, left of
                        it, so the column stays narrow instead of stacking. */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                      }}
                    >
                      {summary.patient_balance > 0 && !hasPendingClaims && (
                        <button
                          type="button"
                          onClick={() =>
                            openInvoicePreview(activeLedger, ledgerView, patient)
                          }
                          title={
                            isFamilyView
                              ? 'Build an invoice from the family ledger (latest 2 days of activity)'
                              : 'Build an invoice from the patient ledger (latest 2 days of activity)'
                          }
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: '#1f5a3d',
                            border: '1px solid #174a31',
                            borderRadius: 6,
                            padding: '6px 12px',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span aria-hidden>🧾</span> Create Invoice
                        </button>
                      )}
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, color: '#6b716d', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          {isFamilyView ? 'Family Balance' : 'Patient Balance'}
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: summary.patient_balance > 0 ? '#b42318' : summary.patient_balance < 0 ? '#1d6b40' : '#1a1d1b',
                          }}
                        >
                          {formatCurrency(summary.patient_balance)}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b716d', marginTop: 2 }}>
                          {summary.patient_balance > 0
                            ? isFamilyView ? 'Family owes' : 'Patient owes'
                            : summary.patient_balance < 0
                            ? 'Credit on account'
                            : 'Settled'}
                        </div>
                      </div>
              </div>
            </div>

                  {/* Summary cards */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                    <SummaryCard label="Production" value={formatCurrency(summary.production)} />
                    <SummaryCard
                      label="Collections"
                      value={formatCurrency(summary.collections)}
                      sub={`Patient ${formatCurrency(summary.collections_patient)} · Insurance ${formatCurrency(
                        summary.collections_insurance
                      )}`}
                    />
                    <SummaryCard
                      label="Adjustments"
                      value={formatCurrency(summary.adjustments)}
                      sub={summary.write_offs ? `Write-offs ${formatCurrency(summary.write_offs)}` : null}
                    />
                    <SummaryCard
                      label="Insurance Claims"
                      value={`${summary.claim_count}`}
                      sub={`Billed ${formatCurrency(summary.claims_billed)} · Paid ${formatCurrency(
                        summary.claims_paid
                      )}`}
                    />
                    <SummaryCard
                      label="Outstanding Insurance"
                      value={formatCurrency(summary.claims_outstanding)}
                      sub={`Estimated ${formatCurrency(summary.claims_estimated)}`}
                      accent={summary.claims_outstanding > 0 ? '#a8520c' : undefined}
                    />
                  </div>

          {/* Filter chips */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: '#6b716d' }}>Show:</span>
            {[
              { id: 'all', label: `All (${(activeLedger.events || []).length})` },
              {
                id: 'transactions',
                label: `Transactions (${(activeLedger.events || []).filter((e) => e.kind === 'transaction').length})`,
              },
              { id: 'claims', label: `Claims (${(activeLedger.events || []).filter((e) => e.kind === 'claim').length})` },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setEventFilter(opt.id)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: eventFilter === opt.id ? '#1f5a3d' : '#e3e7e4',
                  backgroundColor: eventFilter === opt.id ? '#1f5a3d' : '#fff',
                  color: eventFilter === opt.id ? '#fff' : '#3c4540',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
            </div>

          {/* Timeline table */}
          <div
                style={{
              backgroundColor: '#fff',
              border: '1px solid #e3e7e4',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                <tr style={{ backgroundColor: '#f7f8f7', textAlign: 'left' }}>
                  <th style={th}>Date</th>
                  {isFamilyView && (
                    <th
                      style={th}
                      title="Patient this entry was posted to (Open Dental's Family Account view shows the same Initials column)."
                    >
                      Patient
                    </th>
                  )}
                  <th style={th}>Kind</th>
                  <th style={th}>Type / State</th>
                  <th style={th}>Description</th>
                  <th style={th}>Provider</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                  <th
                    style={{ ...th, textAlign: 'right' }}
                    title={
                      isFamilyView
                        ? "Family running balance after this event (sum across every household member, computed bottom-up). Positive = family owes the practice; negative = credit on file."
                        : "Patient's running balance after this event (computed bottom-up). Positive = patient owes the practice; negative = credit on file."
                    }
                  >
                    Balance
                  </th>
                  <th style={{ ...th, width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                {filteredEvents.length === 0 && (
                  <tr>
                    <td colSpan={isFamilyView ? 8 : 7} style={{ padding: 32, textAlign: 'center', color: '#6b716d' }}>
                      No events to show.
                        </td>
                      </tr>
                )}
              {(() => {
                  const ledgerTz = activeLedger?.timezone;
                  // Actions this practice's PMS supports on Newton-posted rows
                  // (Open Dental: provider edit only). Absent = all available.
                  const rowActions = activeLedger?.ledgerActions || {};
                  const canBreakDown = rowActions.breakDown !== false;
                  const canDeleteRow = rowActions.deleteRow !== false;
                  const dateKeyOf = (e) =>
                    dateKeyIn(e.transaction_date || e.claim_time || e.occurred_at, ledgerTz);
                  let prevKey = null;
                  let groupIdx = -1;
                  return filteredEvents.map((event, idx) => {
                    const primary =
                      event.transaction_date || event.claim_time || event.occurred_at;
                    const time = formatTimeIfNotMidnight(event.occurred_at, ledgerTz);
                    const key = dateKeyOf(event);
                    const isFirstOfDay = key !== prevKey;
                    if (isFirstOfDay) groupIdx += 1;
                    prevKey = key;
                    const groupCount = isFirstOfDay
                      ? filteredEvents.filter((e) => dateKeyOf(e) === key).length
                      : 0;
                    const bandColor = groupIdx % 2 === 0 ? '#ffffff' : '#f7f8f7';
                    // Rows Newton posted to the PMS ledger: match the
                    // ``Newton`` marker family (``Newtonai`` auto-post,
                    // ``Newtonbd`` break-down slice, …) or an exact reference
                    // passed from the Invoice tab (catches legacy ``N…`` posts
                    // too). Tinted light green.
                    const cn = event.check_number;
                    const isNewtonPosted = typeof cn === 'string' && !!cn && (
                      cn.startsWith('Newton') ||
                      (newtonReferences && newtonReferences.has(cn))
                    );
                    // Edit / break down / delete run patient-payment
                    // operations, so a Newton-posted insurance (claim) payment
                    // keeps the tint but not the controls.
                    const isInsurancePayment = (event.transaction_subtype || '').toUpperCase() === 'INSURANCE_PAYMENT'
                      || !!event.claim;
                    const canEditNewtonPayment = isNewtonPosted && !isInsurancePayment;
                return (
                  <tr
                    key={`${event.kind}-${event.remote_id || event.name || idx}`}
                    title={isNewtonPosted ? 'Posted by Newton' : undefined}
                    style={{
                      borderTop: isFirstOfDay
                        ? '2px solid #cdd3cf'
                        : '1px solid #eef0ee',
                      backgroundColor: isNewtonPosted ? '#e7f4ec' : bandColor,
                      boxShadow: isNewtonPosted
                        ? 'inset 3px 0 0 0 #1d6b40'
                        : undefined,
                    }}
                  >
                    <td style={td}>
                      {isFirstOfDay ? (
                        <>
                          <div style={{ fontWeight: 600, color: '#16241c' }}>
                            {formatDate(primary, ledgerTz)}
                      </div>
                          <div style={{ fontSize: 11, color: '#8a908c', marginTop: 2 }}>
                            {groupCount} {groupCount === 1 ? 'event' : 'events'}
                      </div>
                          {time && (
                            <div style={{ fontSize: 11, color: '#a3a8a4', marginTop: 2 }}>
                              {time}
                      </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div style={{ color: '#3c4540' }}>{formatDate(primary, ledgerTz)}</div>
                          {time && (
                            <div style={{ fontSize: 11, color: '#8a908c', marginTop: 2 }}>
                              {time}
                      </div>
                          )}
                        </>
                      )}
                    </td>
                    {isFamilyView && (() => {
                      const palette = memberStylesByCid[event.posted_to_contact_id] || {
                        bg: '#eef0ee',
                        fg: '#16241c',
                      };
                      return (
                        <td style={td}>
                          <button
                            type="button"
                            onClick={() => {
                              if (!event.posted_to_contact_id) return;
                              setLedgerView('patient');
                              handleSelectPatient({
                                contact_id: event.posted_to_contact_id,
                                name: event.posted_to_name || `Contact #${event.posted_to_contact_id}`,
                              });
                            }}
                            title={`Posted to ${event.posted_to_name}'s ledger — click to open their patient view`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '2px 6px',
                              borderRadius: 6,
                              border: '1px solid transparent',
                              backgroundColor: 'transparent',
                              cursor: event.posted_to_contact_id ? 'pointer' : 'default',
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                backgroundColor: palette.bg,
                                color: palette.fg,
                                padding: '2px 6px',
                                borderRadius: 4,
                                lineHeight: 1.2,
                              }}
                            >
                              <span data-phi>{event.posted_to_initials || '—'}</span>
                        </span>
                            <span data-phi style={{ fontSize: 12, color: '#3c4540' }}>
                              {event.posted_to_name || '—'}
                            </span>
                          </button>
                        </td>
                      );
                    })()}
                    <td style={td}>
                      <Pill
                        bg={event.kind === 'transaction' ? '#f1ecfb' : '#fdf3e3'}
                        color={event.kind === 'transaction' ? '#5b3fa8' : '#a8520c'}
                      >
                        {event.kind === 'transaction' ? 'Transaction' : 'Claim'}
                      </Pill>
                        </td>
                    <td style={td}>
                      {event.kind === 'transaction' ? (
                        (() => {
                          const s =
                            TXN_TYPE_STYLES[event.transaction_type] || {
                              bg: '#eef0ee',
                              color: '#3c4540',
                              label: event.transaction_type || 'Other',
                            };
                          const subtypeMeaningful = isMeaningfulSubtype(event.transaction_subtype);
                          return (
                            <div>
                              <Pill bg={s.bg} color={s.color} title={event.transaction_subtype || ''}>
                                {s.label}
                                {subtypeMeaningful
                                  ? ` · ${event.transaction_subtype
                                      .replace(/_/g, ' ')
                                      .toLowerCase()
                                      .replace(/\b\w/g, (c) => c.toUpperCase())}`
                                  : ''}
                              </Pill>
                              {(event.related_claim_submit_time || event.related_claim_paid_at) && (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#6b716d',
                                    marginTop: 6,
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {event.related_claim_submit_time && (
                                    <div>Submitted {formatDate(event.related_claim_submit_time, ledgerTz)}</div>
                                  )}
                                  {event.related_claim_paid_at && (
                                    <div style={{ color: '#1d6b40', fontWeight: 500 }}>
                                      Settled {formatDate(event.related_claim_paid_at, ledgerTz)}
                      </div>
                                  )}
                    </div>
                              )}
                  </div>
                );
                        })()
                      ) : (
                        (() => {
                          const s =
                            CLAIM_STATE_STYLES[event.state] || {
                              bg: '#eef0ee',
                              color: '#3c4540',
                              label: event.state || 'Unknown',
                            };
                          return (
                            <Pill bg={s.bg} color={s.color}>
                              {s.label}
                            </Pill>
                          );
                        })()
                      )}
                                </td>
                    <td style={td}>
                      <div style={{ color: '#1a1d1b' }}>
                        {event.kind === 'transaction' ? txnDescription(event) : claimDescription(event)}
                      </div>
                      {event.kind === 'transaction' && event.related_claim_remote_id && (
                        <div style={{ fontSize: 11, color: '#6b716d', marginTop: 2 }}>
                          Claim #{event.related_claim_remote_id}
                          {event.related_claim_state ? ` · ${event.related_claim_state}` : ''}
                          {event.related_claim_billed != null && (
                            <>
                              {' · '}Billed {formatCurrency(event.related_claim_billed)}
                              {event.related_claim_estimated != null && (
                                <> · Est {formatCurrency(event.related_claim_estimated)}</>
                              )}
                    </>
                  )}
                          {event.check_number && <> · Check #{event.check_number}</>}
                    </div>
                      )}
                      {event.kind === 'claim' && (
                        <div style={{ fontSize: 11, color: '#6b716d', marginTop: 2 }}>
                          Billed {formatCurrency(event.total_billed_amount)} · Est{' '}
                          {formatCurrency(event.estimated_insurance_payment)} · Paid{' '}
                          {formatCurrency(event.total_paid_amount)}
                          {event.outstanding_amount > 0 && (
                            <> · Outstanding {formatCurrency(event.outstanding_amount)}</>
                          )}
                  </div>
                      )}
                                </td>
                    <td style={{ ...td, color: '#3c4540' }}>
                      {canEditNewtonPayment && (practicePaymentSettings.ledgerProviders || []).length > 0 ? (
                        editingProviderId === event.remote_id ? (
                          // Unlocked: dropdown + Cancel. Selecting a provider
                          // triggers the PMS write immediately.
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <select
                                value={event.provider_id || ''}
                                disabled={providerSavingId === event.remote_id}
                                title="Change the provider Newton credited this payment to (writes to the PMS)"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleChangeRowProvider(event, e.target.value)}
                                style={{
                                  maxWidth: 150,
                                  fontSize: 12,
                                  padding: '2px 4px',
                                  border: '1px solid #1d6b40',
                                  borderRadius: 4,
                                  background: '#fff',
                                  color: '#1d6b40',
                                  cursor: providerSavingId === event.remote_id ? 'wait' : 'pointer',
                                }}
                              >
                                {/* Preserve the current provider even if it's not
                                    in the practice's configured option list. */}
                                {event.provider_id &&
                                  !(practicePaymentSettings.ledgerProviders || []).some(
                                    (p) => p.id === event.provider_id
                                  ) && (
                                    <option value={event.provider_id}>
                                      {(event.provider_name || event.provider_id) +
                                        ` (${event.provider_id})`}
                                    </option>
                                  )}
                                {(practicePaymentSettings.ledgerProviders || []).map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {(p.name || p.id) + ` (${p.id})`}
                                  </option>
                                ))}
                              </select>
                              {providerSavingId !== event.remote_id && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingProviderId(null);
                                    setProviderEditError(null);
                                  }}
                                  style={{
                                    fontSize: 11,
                                    color: '#6b716d',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                            {providerSavingId === event.remote_id && (
                              <span style={{ fontSize: 10, color: '#6b716d' }}>Saving…</span>
                            )}
                            {providerEditError?.remoteId === event.remote_id && (
                              <span style={{ fontSize: 10, color: '#b42318' }}>
                                {providerEditError.message}
                              </span>
                            )}
                          </div>
                        ) : (
                          // Read-only default: name, then Edit + Break down side
                          // by side. Outlined, not red — red is kept for the
                          // destructive "Delete row"; both still write to the PMS
                          // (see their tooltips).
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                            <span>{event.provider_name || '—'}</span>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                            <button
                              type="button"
                              title="Change provider (writes to the PMS)"
                              onClick={(e) => {
                                e.stopPropagation();
                                setProviderEditError(null);
                                setEditingProviderId(event.remote_id);
                              }}
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: '#1a1d1b',
                                background: '#fff',
                                border: '1px solid #d6dbd7',
                                borderRadius: 6,
                                padding: '2px 8px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Edit provider
                            </button>
                            {canBreakDown && (
                            <button
                              type="button"
                              title="Split this payment across multiple providers (writes to the PMS)"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSplitModal(event);
                              }}
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: '#1a1d1b',
                                background: '#fff',
                                border: '1px solid #d6dbd7',
                                borderRadius: 6,
                                padding: '2px 8px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Break down
                            </button>
                            )}
                            </div>
                          </div>
                        )
                      ) : (
                        event.provider_name || '—'
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {event.kind === 'transaction' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color:
                                event.amount > 0
                                  ? '#b42318'
                                  : event.amount < 0
                                  ? '#1d6b40'
                                  : '#1a1d1b',
                            }}
                          >
                            {formatCurrency(event.amount)}
                                  </span>
                          {event.related_claim_estimated != null &&
                            event.related_claim_estimated > 0 && (
                              <span
                                style={{ fontSize: 11, color: '#8a908c', marginTop: 2 }}
                                title={`Carrier estimated $${event.related_claim_estimated.toFixed(
                                  2
                                )} for claim #${event.related_claim_remote_id}`}
                              >
                                Est {formatCurrency(event.related_claim_estimated)}
                              </span>
                  )}
                </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <span
                            style={{
                              fontWeight: 600,
                              color: event.total_paid_amount > 0 ? '#1d6b40' : '#1a1d1b',
                            }}
                          >
                            {formatCurrency(event.total_paid_amount)}
                                  </span>
                          {event.estimated_insurance_payment > 0 && (
                            <span
                              style={{
                                fontSize: 11,
                                color: event.state === 'PAID' ? '#8a908c' : '#a8520c',
                                marginTop: 2,
                              }}
                              title="Carrier's estimated insurance payment for this claim"
                            >
                              Est {formatCurrency(event.estimated_insurance_payment)}
                            </span>
              )}
            </div>
                      )}
                                </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const rb = event.running_balance;
                        if (rb == null) return <span style={{ color: '#cdd3cf' }}>—</span>;
                        const isOwed = rb > 0.005;
                        const isCredit = rb < -0.005;
                        const color = isOwed ? '#b42318' : isCredit ? '#1d6b40' : '#3c4540';
                        const sign = isCredit ? '−' : '';
                        return (
                          <span
                            style={{ fontWeight: 600, color }}
                            title={
                              isOwed
                                ? 'Patient owes the practice this much after this event'
                                : isCredit
                                ? 'Patient has a credit on file after this event'
                                : 'Patient is settled (zero balance) after this event'
                            }
                          >
                            {sign}
                            {formatCurrency(Math.abs(rb))}
                    </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {canEditNewtonPayment && canDeleteRow ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          <button
                            type="button"
                            title="Delete this Newton-posted payment from the PMS"
                            disabled={deletingRowId === event.remote_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteRow(event);
                            }}
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#fff',
                              background: '#b42318',
                              border: '1px solid #b42318',
                              borderRadius: 4,
                              padding: '1px 6px',
                              cursor: deletingRowId === event.remote_id ? 'default' : 'pointer',
                              opacity: deletingRowId === event.remote_id ? 0.6 : 1,
                            }}
                          >
                            {deletingRowId === event.remote_id ? 'Deleting…' : 'Delete row'}
                          </button>
                          {deleteRowError?.remoteId === event.remote_id && (
                            <span style={{ fontSize: 10, color: '#b42318' }}>
                              {deleteRowError.message}
                            </span>
                          )}
                        </div>
                      ) : null}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                        </tbody>
                      </table>
                  </div>
                </div>
                )}
            </div>
          </div>
              </div>
            )}

      {/* Break down (split) modal — replace one Newton-posted payment with
          several, each credited to a different provider. */}
      {splitModal && (() => {
        const { event, total } = splitModal;
        const allocatedCents = splitLines.reduce(
          (s, l) => s + (Number(l.amount) > 0 ? Math.round(Number(l.amount) * 100) : 0),
          0
        );
        const targetCents = Math.round(total * 100);
        const remainingCents = targetCents - allocatedCents;
        const balanced = remainingCents === 0;
        const provOptions = practicePaymentSettings.ledgerProviders || [];
        return (
          <div
            onClick={closeSplitModal}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15,23,42,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1200,
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                maxWidth: 720,
                width: '100%',
                maxHeight: '85vh',
                overflow: 'auto',
                boxShadow: '0 24px 60px rgba(15,23,42,0.45)',
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #e3e7e4',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3 style={{ margin: 0, fontSize: 16, color: '#16241c' }}>
                  Break down payment
                </h3>
                <button
                  onClick={closeSplitModal}
                  disabled={splitSaving}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 20,
                    cursor: splitSaving ? 'not-allowed' : 'pointer',
                    color: '#6b716d',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: '16px 20px' }}>
                {/* Original payment summary */}
                <div
                  style={{
                    background: '#e7f4ec',
                    border: '1px solid #1d6b40',
                    borderRadius: 8,
                    padding: '10px 12px',
                    marginBottom: 16,
                    fontSize: 13,
                    color: '#1d6b40',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    {formatCurrency(total)} · {event.provider_name || event.provider_id || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: '#1d6b40' }}>
                    {formatDate(event.transaction_date)} · check #{event.check_number}
                  </div>
                </div>

                <p style={{ fontSize: 12, color: '#6b716d', margin: '0 0 12px' }}>
                  Split this payment into separate rows per provider. The lines
                  must add up to {formatCurrency(total)}. This replaces the
                  original payment in the PMS with one payment per line.
                </p>

                {/* Split lines */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {splitLines.map((line, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ position: 'relative', width: 140, flexShrink: 0 }}>
                        <span
                          style={{
                            position: 'absolute',
                            left: 10,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#6b716d',
                            fontSize: 13,
                            pointerEvents: 'none',
                          }}
                        >
                          $
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.amount}
                          disabled={splitSaving}
                          placeholder="0.00"
                          onChange={(e) => updateSplitLine(idx, 'amount', e.target.value)}
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '7px 8px 7px 22px',
                            border: '1px solid #d6dbd7',
                            borderRadius: 6,
                            fontSize: 13,
                          }}
                        />
                      </div>
                      <select
                        value={line.providerId}
                        disabled={splitSaving}
                        onChange={(e) => updateSplitLine(idx, 'providerId', e.target.value)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          boxSizing: 'border-box',
                          padding: '7px 10px',
                          border: '1px solid #d6dbd7',
                          borderRadius: 6,
                          fontSize: 13,
                          background: '#fff',
                        }}
                      >
                        <option value="">Select provider…</option>
                        {event.provider_id &&
                          !provOptions.some((p) => p.id === event.provider_id) && (
                            <option value={event.provider_id}>
                              {(event.provider_name || event.provider_id) +
                                ` (${event.provider_id})`}
                            </option>
                          )}
                        {provOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {(p.name || p.id) + ` (${p.id})`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeSplitLine(idx)}
                        disabled={splitSaving || splitLines.length <= 2}
                        title={splitLines.length <= 2 ? 'At least two lines required' : 'Remove line'}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: 16,
                          color: splitLines.length <= 2 ? '#d6dbd7' : '#b42318',
                          cursor: splitLines.length <= 2 ? 'not-allowed' : 'pointer',
                          padding: '0 4px',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={addSplitLine}
                  disabled={splitSaving}
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#1d6b40',
                    background: '#fff',
                    border: '1px dashed #1d6b40',
                    borderRadius: 6,
                    padding: '4px 10px',
                    cursor: 'pointer',
                  }}
                >
                  + Add line
                </button>

                {/* Running total */}
                <div
                  style={{
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: '1px solid #e3e7e4',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: '#6b716d' }}>
                    Allocated {formatCurrency(allocatedCents / 100)} of {formatCurrency(total)}
                  </span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: balanced ? '#1d6b40' : '#b42318',
                    }}
                  >
                    {balanced
                      ? 'Balanced'
                      : `${remainingCents > 0 ? 'Remaining' : 'Over'} ${formatCurrency(
                          Math.abs(remainingCents) / 100
                        )}`}
                  </span>
                </div>

                {splitError && (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#b42318' }}>
                    {splitError}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: '12px 20px',
                  borderTop: '1px solid #e3e7e4',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  onClick={closeSplitModal}
                  disabled={splitSaving}
                  style={{
                    fontSize: 13,
                    color: '#3c4540',
                    background: '#fff',
                    border: '1px solid #d6dbd7',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: splitSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => submitSplit()}
                  disabled={splitSaving}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: splitSaving ? '#8a908c' : '#b42318',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: splitSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {splitSaving ? 'Breaking down…' : 'Break down payment'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* "Balance doesn't match" confirmation — shown when the split lines
          don't sum to the original amount and staff choose to proceed. */}
      {unbalancedConfirm && (() => {
        const { sumCents, totalCents } = unbalancedConfirm;
        const diff = sumCents - totalCents;
        const over = diff > 0;
        return (
          <div
            onClick={() => !splitSaving && setUnbalancedConfirm(null)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15,23,42,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#fff',
                borderRadius: 12,
                maxWidth: 440,
                width: '100%',
                boxShadow: '0 24px 60px rgba(15,23,42,0.45)',
                padding: 20,
              }}
            >
              <h3 style={{ margin: '0 0 10px', fontSize: 16, color: '#16241c' }}>
                Balance doesn’t match
              </h3>
              <p style={{ fontSize: 13, color: '#3c4540', margin: '0 0 8px', lineHeight: 1.5 }}>
                The split lines total <strong>{formatCurrency(sumCents / 100)}</strong>, but
                the original payment is <strong>{formatCurrency(totalCents / 100)}</strong>.
              </p>
              <p style={{ fontSize: 13, color: '#a8520c', margin: '0 0 4px', lineHeight: 1.5 }}>
                Proceeding will {over ? 'add' : 'remove'}{' '}
                <strong>{formatCurrency(Math.abs(diff) / 100)}</strong>{' '}
                {over ? 'more credit than' : 'less credit than'} the original payment,
                changing this patient’s ledger balance.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
                <button
                  type="button"
                  onClick={() => setUnbalancedConfirm(null)}
                  disabled={splitSaving}
                  style={{
                    fontSize: 13,
                    color: '#3c4540',
                    background: '#fff',
                    border: '1px solid #d6dbd7',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: splitSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Go back
                </button>
                <button
                  type="button"
                  onClick={() => submitSplit({ force: true })}
                  disabled={splitSaving}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: splitSaving ? '#8a908c' : '#b42318',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: splitSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {splitSaving ? 'Breaking down…' : 'Proceed anyway'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Invoice Preview modal — opened from either the patient/family modal
          header ("Create Invoice") or directly from a Latest Settled row's
          row-level Invoice button. Shows the latest 2 days of activity as
          line items and the current balance due, then POSTs to
          /api/payments/invoices on Create. */}
      {invoicePreview && (
        <div
          onClick={closeInvoicePreview}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15,23,42,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: 12,
              width: 'min(820px, 100%)',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 48px rgba(15,23,42,0.25)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e3e7e4',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: '#6b716d', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                  Invoice Preview
                </div>
                <div data-phi style={{ fontSize: 18, fontWeight: 700, color: '#16241c', marginTop: 2 }}>
                  {invoicePreview.name}
                </div>
                {/* In Open Dental every invoice is anchored to the
                    guarantor's account, even when the line items belong to
                    a single dependent. Surface that explicitly so the user
                    isn't surprised the addressee differs from the patient
                    they opened. */}
                {invoicePreview.kind === 'patient' &&
                  invoicePreview.billedToGuarantor &&
                  invoicePreview.onBehalfOfName && (
                    <div
                      style={{
                        marginTop: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#5b3fa8',
                        backgroundColor: '#f1ecfb',
                        border: '1px solid #ddd2f3',
                        padding: '3px 8px',
                        borderRadius: 999,
                      }}
                      title="Open Dental anchors invoices to the guarantor's account, even when the line items are for a dependent."
                    >
                      <span>👤 Bill to guarantor:</span>
                      <span data-phi>{invoicePreview.billToName}</span>
                      <span data-phi style={{ opacity: 0.7, fontWeight: 500 }}>
                        · for {invoicePreview.onBehalfOfName}
                      </span>
                    </div>
                  )}
                {/* Editable activity window. Defaults to the last 2 days
                    of activity (auto), but the user can widen or narrow
                    the range with the From/To inputs. The preview rebuilds
                    on every change — events, carry-over, totals all
                    recompute, and the carry-over math stays correct
                    because it's anchored to the actual current balance. */}
                <div
                  style={{
                    marginTop: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flexWrap: 'wrap',
                    fontSize: 12,
                    color: '#6b716d',
                  }}
                >
                  <span>Activity from</span>
                  <input
                    type="date"
                    value={invoicePreview.dateRange.start || ''}
                    onChange={(e) =>
                      updateInvoiceRange({
                        start: e.target.value,
                        end: invoicePreview.dateRange.end,
                      })
                    }
                    style={{
                      fontSize: 12,
                      padding: '3px 6px',
                      border: '1px solid #cdd3cf',
                      borderRadius: 4,
                      color: '#16241c',
                      backgroundColor: '#fff',
                    }}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={invoicePreview.dateRange.end || ''}
                    onChange={(e) =>
                      updateInvoiceRange({
                        start: invoicePreview.dateRange.start,
                        end: e.target.value,
                      })
                    }
                    style={{
                      fontSize: 12,
                      padding: '3px 6px',
                      border: '1px solid #cdd3cf',
                      borderRadius: 4,
                      color: '#16241c',
                      backgroundColor: '#fff',
                    }}
                  />
                  {!invoicePreview.isDefaultRange && (
                    <button
                      type="button"
                      onClick={() => updateInvoiceRange(null)}
                      title="Reset to the last 2 days of activity (default)"
                      style={{
                        fontSize: 11,
                        color: '#6b716d',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline',
                      }}
                    >
                      reset to last 2 days
                    </button>
                  )}
                  {invoicePreview.events.length === 0 && (
                    <span style={{ color: '#a8520c' }}>
                      · no activity in this window
                    </span>
                  )}
                  {invoicePreview.events.length > 0 &&
                    invoicePreview.dateKeys.length < 2 && (
                      <span style={{ opacity: 0.7 }}>
                        · only 1 day of activity in window
                      </span>
                    )}
                </div>
                {/* Optional single-date override for the Date column. Off by
                    default — when the user opts in, every line-item row
                    (preview AND the persisted invoice) is stamped with the
                    chosen date. Useful when the practice wants the invoice
                    to read as a single statement date instead of showing
                    the underlying multi-day activity. */}
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    fontSize: 12,
                    color: '#3c4540',
                  }}
                >
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={invoiceDateOverride !== null}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Default to the latest activity day in the window
                          // (most "natural" statement date).
                          setInvoiceDateOverride(invoicePreview.dateRange.end);
                        } else {
                          setInvoiceDateOverride(null);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    Override line-item date
                  </label>
                  {invoiceDateOverride !== null && (
                    <>
                      <input
                        type="date"
                        value={invoiceDateOverride}
                        onChange={(e) =>
                          setInvoiceDateOverride(e.target.value || null)
                        }
                        style={{
                          fontSize: 12,
                          padding: '3px 6px',
                          border: '1px solid #cdd3cf',
                          borderRadius: 4,
                          color: '#16241c',
                          backgroundColor: '#fff',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setInvoiceDateOverride(null)}
                        title="Restore each row's actual date"
                        style={{
                          fontSize: 11,
                          color: '#6b716d',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        reset
                      </button>
                      <span style={{ fontSize: 11, color: '#a8520c' }}>
                        Every line item will show {formatDate(invoiceDateOverride)}.
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={closeInvoicePreview}
                  title="Close (Esc)"
                  style={{
                    background: '#fff',
                    border: '1px solid #f5c6c1',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 13,
                    color: '#b42318',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  aria-label="Close invoice preview"
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>×</span> Close this
                </button>
                {/* Close all: dismiss the invoice preview AND the patient/family
                    modal behind it in one click. In the AR Aging modal-only
                    flow this tears the whole stack down (the auto-close effect
                    then fires onClose and unmounts us); in the Ledger page it
                    returns the user to the patient search. */}
                <button
                  type="button"
                  onClick={() => { closeInvoicePreview(); handleClearPatient(); }}
                  title="Close both the invoice and the patient window"
                  style={{
                    background: '#fff',
                    border: '1px solid #f5c6c1',
                    borderRadius: 8,
                    padding: '6px 10px',
                    fontSize: 13,
                    color: '#b42318',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  aria-label="Close all windows"
                >
                  <span style={{ fontSize: 16, lineHeight: 1 }}>⤫</span> Close all
                </button>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {/* Line items */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', backgroundColor: '#f7f8f7' }}>
                    <th style={th}>Date</th>
                    {invoicePreview.kind === 'family' && <th style={th}>Patient</th>}
                    <th style={th}>Description</th>
                    <th style={th}>Provider</th>
                    <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicePreview.events.length === 0 && (
                    <tr>
                      <td
                        colSpan={invoicePreview.kind === 'family' ? 5 : 4}
                        style={{ padding: 24, textAlign: 'center', color: '#6b716d' }}
                      >
                        No line items in the preview window.
                      </td>
                    </tr>
                  )}
                  {invoicePreview.events.map((event, idx) => {
                    const primary =
                      event.transaction_date || event.claim_time || event.occurred_at;
                    // When the user has opted into a single-date override, the
                    // Date column collapses to that one date for every row.
                    // Otherwise we show the row's actual date.
                    const displayDate = invoiceDateOverride || primary;
                    const amount =
                      event.kind === 'transaction'
                        ? Number(event.amount) || 0
                        : Number(event.total_paid_amount) || 0;
                    return (
                      <tr
                        key={`inv-${event.kind}-${event.remote_id || event.name || idx}`}
                        style={{ borderTop: '1px solid #eef0ee' }}
                      >
                        <td style={td}>{formatDate(displayDate)}</td>
                        {invoicePreview.kind === 'family' && (
                          <td style={td}>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#3c4540',
                              }}
                              data-phi
                            >
                              {event.posted_to_name || '—'}
                            </span>
                          </td>
                        )}
                        <td style={td}>
                          <div style={{ color: '#16241c' }}>
                            {event.kind === 'transaction'
                              ? txnDescription(event)
                              : claimDescription(event)}
                          </div>
                          {event.kind === 'transaction' && (isMeaningfulSubtype(event.transaction_subtype) || event.transaction_type) && (
                            <div style={{ fontSize: 11, color: '#6b716d', marginTop: 2 }}>
                              {(event.transaction_type || '').replace(/_/g, ' ')}
                              {isMeaningfulSubtype(event.transaction_subtype)
                                ? ` · ${event.transaction_subtype.replace(/_/g, ' ').toLowerCase()}`
                                : ''}
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, color: '#3c4540' }}>{event.provider_name || '—'}</td>
                        <td
                          style={{
                            ...td,
                            textAlign: 'right',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            color: amount > 0 ? '#b42318' : amount < 0 ? '#1d6b40' : '#1a1d1b',
                          }}
                        >
                          {formatCurrency(amount)}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Carry-over row — events are sorted newest-first, so
                      activity from before the preview window sits at the
                      bottom. Without this row the line items don't
                      reconcile with the actual balance due. */}
                  {Math.abs(invoicePreview.previousBalance || 0) >= 0.005 && (
                    <tr style={{ borderTop: '1px solid #e3e7e4', backgroundColor: '#f7f8f7' }}>
                      <td style={td}>—</td>
                      {invoicePreview.kind === 'family' && (
                        <td style={td}>
                          <span style={{ fontSize: 11, color: '#6b716d' }}>—</span>
                        </td>
                      )}
                      <td style={td}>
                        <div style={{ color: '#3c4540', fontWeight: 600 }}>
                          Previous balance brought forward
                        </div>
                        <div style={{ fontSize: 11, color: '#6b716d', marginTop: 2 }}>
                          Net of all activity before {formatDate(invoicePreview.dateRange.start)}
                        </div>
                      </td>
                      <td style={{ ...td, color: '#3c4540' }}>—</td>
                      <td
                        style={{
                          ...td,
                          textAlign: 'right',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          color:
                            invoicePreview.previousBalance > 0
                              ? '#b42318'
                              : invoicePreview.previousBalance < 0
                              ? '#1d6b40'
                              : '#1a1d1b',
                        }}
                      >
                        {formatCurrency(invoicePreview.previousBalance)}
                      </td>
                    </tr>
                  )}
                  {/* Final reconciliation row — makes it crystal clear
                      that previous balance + activity in this window =
                      the balance due shown above. */}
                  <tr
                    style={{
                      borderTop: '2px solid #cdd3cf',
                      backgroundColor: '#fef9f1',
                    }}
                  >
                    <td style={td}>—</td>
                    {invoicePreview.kind === 'family' && (
                      <td style={td}>
                        <span style={{ fontSize: 11, color: '#6b716d' }}>—</span>
                      </td>
                    )}
                    <td style={td} colSpan={2}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#a8520c',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                        }}
                      >
                        {invoicePreview.kind === 'family' ? 'Family Balance Due' : 'Balance Due'}
                      </div>
                      <div style={{ fontSize: 11, color: '#a8520c', marginTop: 2 }}>
                        Previous balance ({formatCurrency(invoicePreview.previousBalance)}) + this
                        statement ({formatCurrency(invoicePreview.totals.netChange)})
                      </div>
                    </td>
                    <td
                      style={{
                        ...td,
                        textAlign: 'right',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        fontSize: 16,
                        color: invoicePreview.balanceDue > 0 ? '#b42318' : '#16241c',
                      }}
                    >
                      {formatCurrency(invoicePreview.balanceDue)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Subtotals — describe what's IN this preview window. */}
              <div
                style={{
                  marginTop: 16,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    border: '1px solid #e3e7e4',
                    borderRadius: 8,
                    padding: '12px 14px',
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6b716d',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    Reconciliation
                  </div>
                  <Row
                    label="Previous balance"
                    value={formatCurrency(invoicePreview.previousBalance)}
                    color={
                      invoicePreview.previousBalance > 0
                        ? '#b42318'
                        : invoicePreview.previousBalance < 0
                        ? '#1d6b40'
                        : '#1a1d1b'
                    }
                  />
                  <Row label="Production" value={formatCurrency(invoicePreview.totals.production)} />
                  <Row
                    label="Adjustments"
                    value={formatCurrency(invoicePreview.totals.adjustments)}
                    color={invoicePreview.totals.adjustments < 0 ? '#1d6b40' : '#1a1d1b'}
                  />
                  <Row
                    label="Insurance Payments"
                    value={formatCurrency(invoicePreview.totals.insurancePayments)}
                    color="#1d6b40"
                  />
                  <Row
                    label="Patient Payments"
                    value={formatCurrency(invoicePreview.totals.patientPayments)}
                    color="#1d6b40"
                  />
                  <div style={{ height: 6 }} />
                  <Row
                    label="Net change this statement"
                    value={formatCurrency(invoicePreview.totals.netChange)}
                    color={
                      invoicePreview.totals.netChange > 0
                        ? '#b42318'
                        : invoicePreview.totals.netChange < 0
                        ? '#1d6b40'
                        : '#1a1d1b'
                    }
                  />
                  <Row
                    label="= Balance due"
                    value={formatCurrency(invoicePreview.balanceDue)}
                    color={invoicePreview.balanceDue > 0 ? '#b42318' : '#16241c'}
                  />
                </div>

                <div
                  style={{
                    border: '1px solid #f3d9ae',
                    backgroundColor: '#fef9f1',
                    borderRadius: 8,
                    padding: '12px 14px',
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: '#a8520c',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {invoicePreview.kind === 'family' ? 'Family Balance Due' : 'Patient Balance Due'}
                  </div>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: invoicePreview.balanceDue > 0 ? '#b42318' : '#16241c',
                    }}
                  >
                    {formatCurrency(invoicePreview.balanceDue)}
                  </div>
                  <div style={{ fontSize: 11, color: '#a8520c', marginTop: 4 }}>
                    Total currently owed across the {invoicePreview.kind === 'family' ? 'household' : 'patient'} ledger
                    (not just this statement window).
                  </div>
                </div>
              </div>
            </div>

            {/* Inline status banner — appears between the body and footer
                after a Create Invoice attempt. Shows the new invoice id +
                a one-click copy of the public payment link on success, or
                a retryable error message on failure. */}
            {invoiceCreated && (
              <div
                style={{
                  padding: '12px 20px',
                  borderTop: '1px solid',
                  borderColor: invoiceCreated.success ? '#c6e3d0' : '#f5c6c1',
                  backgroundColor: invoiceCreated.success ? '#e7f4ec' : '#fef5f4',
                  color: invoiceCreated.success ? '#1d6b40' : '#912018',
                  fontSize: 13,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                {invoiceCreated.success ? (
                  <>
                    <strong>Invoice created.</strong>
                    {invoiceCreated.invoice?.id && (
                      <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                        {invoiceCreated.invoice.id}
                      </span>
                    )}
                    {invoiceCreated.invoice?.publicHash && (
                      <button
                        type="button"
                        onClick={() => {
                          const url = `${FRONTEND_BASE_URL}/invoice/${invoiceCreated.invoice.publicHash}`;
                          try {
                            navigator.clipboard.writeText(url);
                          } catch (e) {
                            console.warn('Clipboard write failed', e);
                          }
                        }}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#fff',
                          backgroundColor: '#1d6b40',
                          border: '1px solid #1d6b40',
                          borderRadius: 6,
                          padding: '4px 10px',
                          cursor: 'pointer',
                        }}
                      >
                        Copy Payment Link
                      </button>
                    )}
                    {invoiceCreated.invoice?.publicHash && (
                      <a
                        href={`${FRONTEND_BASE_URL}/invoice/${invoiceCreated.invoice.publicHash}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#1d6b40',
                          textDecoration: 'underline',
                        }}
                      >
                        Open ↗
                      </a>
                    )}
                  </>
                ) : (
                  <span>
                    <strong>Error:</strong> {invoiceCreated.error}
                  </span>
                )}
              </div>
            )}

            {/* Footer */}
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #e3e7e4',
                backgroundColor: '#f7f8f7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, color: '#6b716d' }}>
                  The invoice charges <strong data-phi>{invoicePreview.billToName}</strong>
                  {invoicePreview.billedToGuarantor && invoicePreview.kind === 'patient'
                    ? <> (guarantor for <span data-phi>{invoicePreview.onBehalfOfName}</span>)</>
                    : invoicePreview.kind === 'family'
                    ? ' (family guarantor)'
                    : ''}
                  {' '}for the full {invoicePreview.kind === 'family' ? 'family' : 'patient'} balance
                  ({formatCurrency(invoicePreview.balanceDue)}).
                </div>
                {practicePaymentSettings.autoChargeOnFileEnabled && previewDefaultCard && (
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: '#16241c',
                      cursor: creatingInvoice ? 'wait' : 'pointer',
                    }}
                    title="When checked, we'll charge this card immediately on invoice creation."
                  >
                    <input
                      type="checkbox"
                      checked={autoChargeRequested}
                      disabled={creatingInvoice}
                      onChange={(e) => setAutoChargeRequested(e.target.checked)}
                    />
                    <span>
                      Auto-charge {(previewDefaultCard.card?.brand || 'card').replace(/_/g, ' ')}
                      {previewDefaultCard.card?.last4 ? ` •• ${previewDefaultCard.card.last4}` : ''}
                      {' '}({formatCurrency(invoicePreview.balanceDue)})
                    </span>
                  </label>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={closeInvoicePreview}
                  style={{
                    fontSize: 13,
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: '1px solid #f5c6c1',
                    backgroundColor: '#fff',
                    color: '#b42318',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
                {(() => {
                  const noBalance = !(invoicePreview.balanceDue > 0);
                  const noLines = invoicePreview.events.length === 0;
                  const alreadyCreated = invoiceCreated?.success === true;
                  const disabled =
                    creatingInvoice || noBalance || noLines || alreadyCreated;
                  let label = 'Create Invoice';
                  if (creatingInvoice) label = 'Creating…';
                  else if (alreadyCreated) label = 'Invoice Created';
                  let title;
                  if (alreadyCreated) title = 'Invoice already created — close this dialog or open a different patient.';
                  else if (noBalance) title = 'Nothing to invoice — balance is $0 or credit.';
                  else if (noLines) title = 'No line items in the preview window.';
                  else title = `Create an invoice for ${formatCurrency(invoicePreview.balanceDue)}`;
                  // "Create & Charge" — only for non-surcharging practices, and
                  // only when a charge handoff is wired (Invoices tab). Creates
                  // the invoice then jumps straight to the terminal charge flow.
                  const showChargeBtn = !practicePaymentSettings.surchargeEnabled && !!onRequestCharge;
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => handleCreateInvoice()}
                        disabled={disabled}
                        title={title}
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          padding: '8px 14px',
                          borderRadius: 6,
                          border: '1px solid',
                          borderColor: disabled ? '#cdd3cf' : '#1f4e79',
                          backgroundColor: disabled ? '#e3e7e4' : '#1f5a3d',
                          color: disabled ? '#6b716d' : '#fff',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span aria-hidden>🧾</span> {label}
                      </button>
                      {showChargeBtn && (
                        <button
                          type="button"
                          onClick={() => handleCreateInvoice({ chargeAfter: true })}
                          disabled={disabled}
                          title={
                            disabled
                              ? title
                              : `Create the invoice and charge ${formatCurrency(invoicePreview.balanceDue)} on a terminal now`
                          }
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            padding: '8px 14px',
                            borderRadius: 6,
                            border: '1px solid',
                            borderColor: disabled ? '#cdd3cf' : '#1d6b40',
                            backgroundColor: disabled ? '#e3e7e4' : '#1d6b40',
                            color: disabled ? '#6b716d' : '#fff',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span aria-hidden>💳</span> Create &amp; Charge
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate-pending confirmation dialog.
          Shown when the backend returns {conflict: true} after a Create
          Invoice attempt. "Yes" supersedes the existing pending invoice
          and creates a fresh one; "No" leaves both intact. Stacks on top
          of the invoice preview modal (zIndex 1200 > 1100). */}
      {pendingConflict && (
        <div
          onClick={() => setPendingConflict(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15,23,42,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#fff',
              borderRadius: 12,
              width: 'min(440px, 100%)',
              boxShadow: '0 24px 48px rgba(15,23,42,0.3)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '20px 24px 16px' }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#16241c',
                  marginBottom: 8,
                }}
              >
                Pending invoice already exists
              </div>
              <div style={{ fontSize: 13, color: '#3c4540', lineHeight: 1.5 }}>
                There is already a pending invoice for{' '}
                <strong>
                  {pendingConflict.existing.patientName ||
                    invoicePreview?.billToName ||
                    'this patient'}
                </strong>
                {typeof pendingConflict.existing.total === 'number' && (
                  <>
                    {' '}for <strong>{formatCurrency(pendingConflict.existing.total)}</strong>
                  </>
                )}
                {(() => {
                  // parseBackendDate handles both new (timezone-aware)
                  // and legacy (naive UTC) timestamps from the payments
                  // backend. New writes from server.now_iso() include a
                  // tz designator; older rows in Cosmos do not.
                  const d = parseBackendDate(pendingConflict.existing.createdAt);
                  if (!d) return null;
                  return (
                    <>
                      {' '}created on{' '}
                      <strong>
                        {d.toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          timeZoneName: 'short',
                        })}
                      </strong>
                    </>
                  );
                })()}
                .
                {pendingConflict.existingCount > 1 && (
                  <>
                    {' '}(
                    {pendingConflict.existingCount} pending invoices total — all
                    will be replaced.)
                  </>
                )}
                {pendingConflict.existing.publicHash && (
                  <div style={{ marginTop: 10 }}>
                    <a
                      href={`${FRONTEND_BASE_URL}/invoice/${pendingConflict.existing.publicHash}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#1f4e79',
                        textDecoration: 'none',
                        border: '1px solid #c5d8ea',
                        backgroundColor: '#f3f7fb',
                        borderRadius: 6,
                        padding: '6px 10px',
                      }}
                    >
                      View existing invoice
                      <span aria-hidden>↗</span>
                    </a>
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  Do you want to proceed? The existing pending invoice
                  will be marked as <em>superseded</em> and a new one
                  created in its place.
                </div>
              </div>
            </div>
            <div
              style={{
                padding: '12px 20px',
                borderTop: '1px solid #e3e7e4',
                backgroundColor: '#f7f8f7',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setPendingConflict(null)}
                disabled={creatingInvoice}
                style={{
                  fontSize: 13,
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: '1px solid #d6dbd7',
                  backgroundColor: '#fff',
                  color: '#3c4540',
                  cursor: creatingInvoice ? 'not-allowed' : 'pointer',
                }}
              >
                No, keep existing
              </button>
              <button
                type="button"
                onClick={() => handleCreateInvoice({ supersede: true, chargeAfter: !!pendingConflict?.chargeAfter })}
                disabled={creatingInvoice}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: '1px solid',
                  borderColor: creatingInvoice ? '#cdd3cf' : '#b42318',
                  backgroundColor: creatingInvoice ? '#e3e7e4' : '#b42318',
                  color: creatingInvoice ? '#6b716d' : '#fff',
                  cursor: creatingInvoice ? 'not-allowed' : 'pointer',
                }}
              >
                {creatingInvoice ? 'Replacing…' : 'Yes, replace'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send payment link modal — offered after a statement invoice is
          created. SMS / Email / Both, gated on the contact details we have. */}
      {sendLinkModal && (() => {
        const hasPhone = !!sendLinkModal.phone;
        const hasEmail = !!sendLinkModal.email;
        const busy = !!sendingChannel;
        const closeModal = () => {
          if (busy) return;
          setSendLinkModal(null);
          setSendFeedback(null);
        };
        const channelBtn = (channel, label, bg, enabled) => (
          <button
            type="button"
            onClick={() => handleSendLink(channel)}
            disabled={busy || !enabled}
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 700,
              padding: '11px 12px',
              borderRadius: 8,
              border: 'none',
              color: '#fff',
              backgroundColor: !enabled ? '#cdd3cf' : bg,
              cursor: busy || !enabled ? 'not-allowed' : 'pointer',
              opacity: sendingChannel && sendingChannel !== channel ? 0.6 : 1,
            }}
          >
            {sendingChannel === channel ? 'Sending…' : label}
          </button>
        );
        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1100,
            }}
            onClick={closeModal}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#fff',
                padding: 24,
                borderRadius: 12,
                maxWidth: 460,
                width: '90%',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              }}
            >
              <h3 style={{ margin: '0 0 6px 0', color: '#16241c', fontSize: 18 }}>
                Send payment link?
              </h3>
              <p style={{ margin: '0 0 16px 0', color: '#6b716d', fontSize: 14 }}>
                Invoice created for <strong data-phi>{sendLinkModal.patientName || 'this patient'}</strong>.
                Send the payment link over SMS, email, or both.
              </p>

              <div
                style={{
                  backgroundColor: '#f7f8f7',
                  border: '1px solid #e3e7e4',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: '#6b716d' }}>Phone</span>
                  <span data-phi style={{ fontWeight: 600, color: hasPhone ? '#16241c' : '#b42318' }}>
                    {hasPhone ? sendLinkModal.phone : 'None on file'}
                  </span>
                </div>
                <div style={{ height: 6 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: '#6b716d' }}>Email</span>
                  <span data-phi style={{ fontWeight: 600, color: hasEmail ? '#16241c' : '#b42318' }}>
                    {hasEmail ? sendLinkModal.email : 'None on file'}
                  </span>
                </div>
              </div>

              {sendFeedback && (
                <div
                  style={{
                    marginBottom: 14,
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    border: '1px solid',
                    borderColor: sendFeedback.type === 'success' ? '#c6e3d0' : '#f5c6c1',
                    backgroundColor: sendFeedback.type === 'success' ? '#e7f4ec' : '#fef5f4',
                    color: sendFeedback.type === 'success' ? '#1d6b40' : '#912018',
                  }}
                >
                  {sendFeedback.message}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                {channelBtn('sms', 'SMS', '#1d6b40', hasPhone)}
                {channelBtn('email', 'Email', '#5b3fa8', hasEmail)}
                {channelBtn('both', 'Both', '#0f766e', hasPhone && hasEmail)}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={busy}
                  style={{
                    fontSize: 13,
                    padding: '8px 14px',
                    borderRadius: 6,
                    border: '1px solid #d6dbd7',
                    backgroundColor: '#fff',
                    color: '#3c4540',
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const Row = ({ label, value, color }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      borderBottom: '1px dashed #eef0ee',
    }}
  >
    <span style={{ color: '#3c4540' }}>{label}</span>
    <span style={{ fontWeight: 600, color: color || '#16241c' }}>{value}</span>
  </div>
);

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const isoToLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const localDateToIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isSameYmd = (a, b) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const calIconBtnStyle = {
  border: '1px solid #e3e7e4',
  background: '#fff',
  width: 28,
  height: 28,
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 16,
  color: '#3c4540',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  lineHeight: 1,
  padding: 0,
};

const calQuickBtnStyle = {
  background: 'none',
  border: 'none',
  fontSize: 11,
  fontWeight: 600,
  padding: '4px 8px',
  borderRadius: 6,
  fontFamily: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const DatePopover = ({ value, max, isLatest, disabled, onChange, onSelectLatest }) => {
  const [open, setOpen] = useState(false);
  const initial = isoToLocalDate(value) || new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const popRef = useRef(null);

  // Snap calendar to the selected date's month each time we open.
  useEffect(() => {
    if (!open) return;
    const d = isoToLocalDate(value) || new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = isoToLocalDate(value);
  const today = new Date();
  const maxDate = isoToLocalDate(max);

  // 6 × 7 calendar grid, prefilled with leading days from the prior month.
  const cells = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const startOffset = first.getDay();
    const arr = [];
    for (let i = 0; i < 42; i += 1) {
      arr.push(new Date(viewYear, viewMonth, 1 - startOffset + i));
    }
    return arr;
  }, [viewYear, viewMonth]);

  const goPrev = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };
  const goNext = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const nextDisabled = (() => {
    if (!maxDate) return false;
    const nextMonthFirst = new Date(viewYear, viewMonth + 1, 1);
    const maxMonthFirst = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
    return nextMonthFirst > maxMonthFirst;
  })();

  const triggerLabel = selected
    ? selected.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Pick a date';

  return (
    <div ref={popRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="pm-chip"
        data-open={open ? 'true' : 'false'}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Icon name="calendar" size={15} />
        <span>{selected ? `Settled ${triggerLabel}` : triggerLabel}</span>
        {isLatest && <span className="pm-chip-badge">Latest</span>}
        <Icon name="chevron-down" size={14} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Pick a settlement date"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 50,
            background: '#fff',
            border: '1px solid #e3e7e4',
            borderRadius: 12,
            boxShadow: '0 16px 40px rgba(15,23,42,0.18)',
            padding: 12,
            width: 280,
          }}
        >
          {/* Month navigation */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
              <button
              type="button"
              onClick={goPrev}
              aria-label="Previous month"
              style={calIconBtnStyle}
            >
              ‹
            </button>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#16241c' }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </div>
            <button
              type="button"
              onClick={goNext}
              disabled={nextDisabled}
              aria-label="Next month"
              style={{
                ...calIconBtnStyle,
                opacity: nextDisabled ? 0.35 : 1,
                cursor: nextDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              ›
            </button>
          </div>

          {/* Weekday header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 2,
              marginBottom: 4,
            }}
          >
            {WEEKDAY_LABELS.map((w, i) => (
              <div
                key={i}
                style={{
                  textAlign: 'center',
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#8a908c',
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  padding: '4px 0',
                }}
              >
                {w}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 2,
            }}
          >
            {cells.map((d, i) => {
              const inMonth = d.getMonth() === viewMonth;
              const isSel = isSameYmd(d, selected);
              const isToday = isSameYmd(d, today);
              const isFuture = !!(maxDate && d > maxDate);
              const cellDisabled = isFuture;
              const bg = isSel ? '#1f5a3d' : 'transparent';
              const color = isSel
                ? '#fff'
                : !inMonth || isFuture
                ? '#cdd3cf'
                : '#16241c';
              return (
                <button
                  key={i}
                  type="button"
                  disabled={cellDisabled}
                onClick={() => {
                    onChange(localDateToIso(d));
                    setOpen(false);
                  }}
                  onMouseEnter={(e) => {
                    if (!isSel && !cellDisabled) e.currentTarget.style.backgroundColor = '#eef0ee';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSel) e.currentTarget.style.backgroundColor = 'transparent';
                }}
                style={{
                  border: 'none',
                    background: bg,
                    color,
                    fontSize: 12,
                    fontWeight: isSel || isToday ? 700 : 500,
                    padding: '8px 0',
                    borderRadius: 8,
                    cursor: cellDisabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    boxShadow: isToday && !isSel ? 'inset 0 0 0 1px #1f5a3d' : 'none',
                    transition: 'background-color 80ms ease',
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px solid #eef0ee',
            }}
          >
            <button
              type="button"
              onClick={() => {
                onSelectLatest();
                setOpen(false);
              }}
              disabled={isLatest}
              title="Use the most recent day with PAID claims"
              style={{
                ...calQuickBtnStyle,
                color: isLatest ? '#cdd3cf' : '#1f5a3d',
                cursor: isLatest ? 'default' : 'pointer',
              }}
            >
              ↻ Latest
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(localDateToIso(today));
                setOpen(false);
              }}
              style={{
                ...calQuickBtnStyle,
                color: '#1f5a3d',
                  cursor: 'pointer',
                }}
              >
              Today
              </button>
            </div>
          </div>
      )}
        </div>
  );
};

// textAlign is explicit: without it plain cells inherit CRA's centred
// `.App` while flex-laid-out cells sit left, so one column mixes both.
const th = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 11,
  fontWeight: 600,
  color: '#6b716d',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td = {
  textAlign: 'left',
  padding: '12px',
  verticalAlign: 'top',
};

export default LedgerTab;
